import { Routes, ActivatedRouteSnapshot } from "@angular/router";
import { inject } from "@angular/core";
import { SubdomainService } from "./shared/services/subdomain.service";
import { LakeClarityTrackerCategoryEnum } from "./shared/generated/enum/lake-clarity-tracker-category-enum";
import { UnsavedChangesGuard } from "./shared/guards/unsaved-changes.guard";
import { UnauthenticatedAccessGuard } from "./shared/guards/unauthenticated-access/unauthenticated-access.guard";
import { EipAdminAccessGuard } from "./shared/guards/eip-admin-access.guard";
import { ClimateResilienceAdminAccessGuard } from "./shared/guards/climate-resilience-admin-access.guard";
import { ProjectUpdateViewAccessGuard } from "./shared/guards/project-update-view-access.guard";
import { TransportationAdminAccessGuard } from "./shared/guards/transportation-admin-access.guard";
import { StormwaterAdminAccessGuard } from "./shared/guards/stormwater-admin-access.guard";
import { StormwaterEditAccessGuard } from "./shared/guards/stormwater-edit-access.guard";
import { StormwaterManageRoadSegmentAccessGuard } from "./shared/guards/stormwater-manage-road-segment-access.guard";
import { ParcelTrackerAdminAccessGuard } from "./shared/guards/parcel-tracker-admin-access.guard";
import { ParcelTrackerPowerAccessGuard } from "./shared/guards/parcel-tracker-power-access.guard";
import { EnterPermitDetailsPrerequisiteGuard } from "./areas/parcels/parcel-permits/parcel-permit-workflow/shared/enter-permit-details-prerequisite.guard";
import { ParcelTrackerPrivilegedViewAccessGuard } from "./shared/guards/parcel-tracker-privileged-view-access.guard";
import { ParcelPermitsAccessGuard } from "./shared/guards/parcel-permits-access.guard";
import { areaHostGuard } from "./shared/guards/area-host.guard";
import { AREA_CONFIGS, getAreaTitlePrefix } from "./shared/area-config";
import { environment } from "src/environments/environment";
import { legacyRedirectRoutes } from "./legacy-redirect-routes";

export const routeParams = {
    definitionID: "definitionID",
    projectID: "projectID",
    projectNumber: "projectNumber",
    onlandVisualTrashAssessmentID: "onlandVisualTrashAssessmentID",
    onlandVisualTrashAssessmentAreaID: "onlandVisualTrashAssessmentAreaID",
    treatmentBMPID: "treatmentBMPID",
    treatmentBMPVersionID: "treatmentBMPVersionID",
    observationTypeID: "observationTypeID",
    loadGeneratingUnitID: "loadGeneratingUnitID",
    jurisdictionID: "jurisdictionID",
    organizationID: "organizationID",
    fundingSourceID: "fundingSourceID",
    eipFocusAreaID: "eipFocusAreaID",
    eipProgramID: "eipProgramID",
    eipActionPriorityID: "eipActionPriorityID",
    eipPerformanceMeasureID: "eipPerformanceMeasureID",
    thresholdCategoryID: "thresholdCategoryID",
    thresholdReportingCategoryID: "thresholdReportingCategoryID",
    thresholdIndicatorID: "thresholdIndicatorID",
    thresholdEvaluationPeriodID: "thresholdEvaluationPeriodID",
};

function getAreaLayoutComponent() {
    const subdomainService = inject(SubdomainService);
    const subdomain = subdomainService.getSubdomain();
    switch (subdomain) {
        case "stormwater":
            return import("./areas/stormwater/stormwater-layout.component").then((m) => m.StormwaterLayoutComponent);
        case "climate":
            return import("./areas/climate/climate-layout.component").then((m) => m.ClimateLayoutComponent);
        case "eip":
            return import("./areas/eip/eip-layout.component").then((m) => m.EipLayoutComponent);
        case "clarity":
            return import("./areas/clarity/clarity-layout.component").then((m) => m.ClarityLayoutComponent);
        case "monitoring":
            return import("./areas/monitoring/monitoring-layout.component").then((m) => m.MonitoringLayoutComponent);
        case "parcels":
            return import("./areas/parcels/parcels-layout.component").then((m) => m.ParcelsLayoutComponent);
        case "marketplace":
            return import("./areas/marketplace/marketplace-layout.component").then((m) => m.MarketplaceLayoutComponent);
        case "threshold":
            return import("./areas/threshold/threshold-layout.component").then((m) => m.ThresholdLayoutComponent);
        case "transportation":
            return import("./areas/transportation/transportation-layout.component").then((m) => m.TransportationLayoutComponent);
        case "ltinfo":
            return import("./pages/site-layout/site-layout.component").then((m) => m.SiteLayoutComponent);
        default:
            return import("./pages/site-layout/site-layout.component").then((m) => m.SiteLayoutComponent);
    }
}

export const routes: Routes = [
    { path: "create-user-callback", loadComponent: () => import("./pages/create-user-callback/create-user-callback.component").then((m) => m.CreateUserCallbackComponent) },
    { path: "signin-oidc", loadComponent: () => import("./pages/login-callback/login-callback.component").then((m) => m.LoginCallbackComponent) },
    {
        path: "",
        // Root browser-tab prefix. Parcel Tracker uses its own area name so tabs read
        // "Parcel Tracker | {Page}"; all other areas keep the legacy "LT Info" prefix.
        // PageTitleStrategy joins this with child route titles and dedupes, so the area
        // home page (whose own title is "Parcel Tracker") doesn't double up.
        title: () => getAreaTitlePrefix(inject(SubdomainService).getSubdomain()),
        loadComponent: () => getAreaLayoutComponent(),
        // The layout above is picked from the hostname, so a route tagged with another area's data.area
        // is bounced to that area's host rather than rendered under the wrong nav.
        canActivateChild: [areaHostGuard],
        children: [
            {
                path: "",
                loadComponent: () => {
                    const subdomainService = inject(SubdomainService);
                    const subdomain = subdomainService.getSubdomain();
                    switch (subdomain) {
                        case "stormwater":
                            return import("./areas/stormwater/home.component").then((m) => m.StormwaterHomeComponent);
                        case "climate":
                            return import("./areas/climate/home.component").then((m) => m.ClimateHomeComponent);
                        case "eip":
                            return import("./areas/eip/home.component").then((m) => m.EipHomeComponent);
                        case "clarity":
                            return import("./areas/clarity/home.component").then((m) => m.ClarityHomeComponent);
                        case "monitoring":
                            return import("./areas/monitoring/home.component").then((m) => m.MonitoringHomeComponent);
                        case "parcels":
                            return import("./areas/parcels/home.component").then((m) => m.ParcelsHomeComponent);
                        case "marketplace":
                            return import("./areas/marketplace/home.component").then((m) => m.MarketplaceHomeComponent);
                        case "threshold":
                            return import("./areas/threshold/home.component").then((m) => m.ThresholdHomeComponent);
                        case "transportation":
                            return import("./areas/transportation/home.component").then((m) => m.TransportationHomeComponent);
                        case "ltinfo":
                        case "main":
                            return import("./pages/home/home-index/home-index.component").then((m) => m.HomeIndexComponent);
                        default:
                            return import("./pages/home/home-index/home-index.component").then((m) => m.HomeIndexComponent);
                    }
                },
                title: () => {
                    const subdomainService = inject(SubdomainService);
                    const subdomain = subdomainService.getSubdomain();
                    const config = AREA_CONFIGS.find((a) => a.subdomain === subdomain) || AREA_CONFIGS[0];
                    return config.displayName || "";
                },
            },
            // Add your other child routes here, e.g.:
            {
                path: "about",
                title: "About",
                loadComponent: () => {
                    const subdomainService = inject(SubdomainService);
                    const subdomain = subdomainService.getSubdomain();
                    switch (subdomain) {
                        case "climate":
                            return import("./areas/climate/about.component").then((m) => m.ClimateAboutComponent);
                        case "clarity":
                            return import("./areas/clarity/about/about.component").then((m) => m.ClarityAboutComponent);
                        case "monitoring":
                            return import("./areas/monitoring/about.component").then((m) => m.MonitoringAboutComponent);
                        case "parcels":
                            return import("./areas/parcels/about.component").then((m) => m.ParcelsAboutComponent);
                        case "marketplace":
                            return import("./areas/marketplace/about.component").then((m) => m.MarketplaceAboutComponent);
                        case "threshold":
                            return import("./areas/threshold/about.component").then((m) => m.ThresholdAboutComponent);
                        case "transportation":
                            return import("./areas/transportation/about.component").then((m) => m.TransportationAboutComponent);
                        case "stormwater":
                            return import("./areas/stormwater/home.component").then((m) => m.StormwaterHomeComponent);
                        case "ltinfo":
                        case "main":
                            return import("./pages/about/about.component").then((m) => m.AboutComponent);
                        default:
                            return import("./pages/about/about.component").then((m) => m.AboutComponent);
                    }
                },
            },
            // Parcel Tracker area routes
            {
                path: "tracked-parcels",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Tracked Parcels",
                loadComponent: () => import("./areas/parcels/parcels/tracked-parcels.component").then((m) => m.TrackedParcelsComponent),
                data: { area: "parcels" },
            },
            {
                path: "all-parcels",
                canActivate: [UnauthenticatedAccessGuard],
                title: "All Parcels",
                loadComponent: () => import("./areas/parcels/parcels/all-parcels.component").then((m) => m.AllParcelsComponent),
                data: { area: "parcels" },
            },
            {
                path: "parcels/:parcelNumber/manage-development-rights",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Manage Development Rights",
                loadComponent: () => import("./areas/parcels/parcels/manage-development-rights/manage-development-rights-outlet.component").then((m) => m.ManageDevelopmentRightsOutletComponent),
                data: { area: "parcels" },
                children: [
                    { path: "", redirectTo: "instructions", pathMatch: "full" },
                    {
                        path: "instructions",
                        loadComponent: () => import("./areas/parcels/parcels/manage-development-rights/manage-development-rights-instructions.component").then((m) => m.ManageDevelopmentRightsInstructionsComponent),
                    },
                    {
                        path: "existing-inventory",
                        loadComponent: () => import("./areas/parcels/parcels/manage-development-rights/manage-development-rights-inventory.component").then((m) => m.ManageDevelopmentRightsInventoryComponent),
                    },
                    {
                        path: "exemptions",
                        loadComponent: () => import("./areas/parcels/parcels/manage-development-rights/manage-development-rights-exemptions.component").then((m) => m.ManageDevelopmentRightsExemptionsComponent),
                    },
                ],
            },
            {
                path: "parcels/:parcelNumber/ipes-scores/new",
                canActivate: [UnauthenticatedAccessGuard],
                title: "New IPES Score",
                loadComponent: () => import("./areas/parcels/ipes-score-workflow/ipes-score-workflow-outlet.component").then((m) => m.IpesScoreWorkflowOutletComponent),
                data: { area: "parcels" },
                children: [
                    { path: "", redirectTo: "basics", pathMatch: "full" },
                    {
                        path: "basics",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () => import("./areas/parcels/ipes-score-workflow/steps/basics/ipes-basics-step.component").then((m) => m.IpesBasicsStepComponent),
                    },
                ],
            },
            {
                path: "parcels/:parcelNumber/ipes-scores/:ipesScoreID/edit",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Edit IPES Score",
                loadComponent: () => import("./areas/parcels/ipes-score-workflow/ipes-score-workflow-outlet.component").then((m) => m.IpesScoreWorkflowOutletComponent),
                data: { area: "parcels" },
                children: [
                    { path: "", redirectTo: "basics", pathMatch: "full" },
                    {
                        path: "basics",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () => import("./areas/parcels/ipes-score-workflow/steps/basics/ipes-basics-step.component").then((m) => m.IpesBasicsStepComponent),
                    },
                    {
                        path: "parcel-information",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () => import("./areas/parcels/ipes-score-workflow/steps/parcel-information/ipes-parcel-info-step.component").then((m) => m.IpesParcelInfoStepComponent),
                    },
                    {
                        path: "soil-hydrology",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () => import("./areas/parcels/ipes-score-workflow/steps/soil-hydrology/ipes-soil-hydrology-step.component").then((m) => m.IpesSoilHydrologyStepComponent),
                    },
                    {
                        path: "access-utilities",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () => import("./areas/parcels/ipes-score-workflow/steps/access-utilities/ipes-access-utilities-step.component").then((m) => m.IpesAccessUtilitiesStepComponent),
                    },
                    {
                        path: "score-summary",
                        loadComponent: () => import("./areas/parcels/ipes-score-workflow/steps/score-summary/ipes-score-summary-step.component").then((m) => m.IpesScoreSummaryStepComponent),
                    },
                    {
                        path: "land-capability-verification",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () => import("./areas/parcels/ipes-score-workflow/steps/land-capability/ipes-land-capability-step.component").then((m) => m.IpesLandCapabilityStepComponent),
                    },
                ],
            },
            {
                path: "parcels/:parcelNumber",
                title: "Parcel Detail",
                loadComponent: () => import("./areas/parcels/parcels/parcel-detail/parcel-detail.component").then((m) => m.ParcelDetailComponent),
                data: { area: "parcels" },
            },
            {
                path: "parcels/:parcelNumber/fact-sheet",
                title: "Parcel Fact Sheet",
                loadComponent: () => import("./areas/parcels/parcels/parcel-fact-sheet/parcel-fact-sheet.component").then((m) => m.ParcelFactSheetComponent),
                data: { area: "parcels" },
            },
            {
                path: "manage/parcels",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Manage Parcels",
                loadComponent: () => import("./areas/parcels/manage-parcels/manage-parcels.component").then((m) => m.ManageParcelsComponent),
                data: { area: "parcels" },
            },
            {
                path: "manage/parcel-management-guidance",
                title: "Parcel Management Guidance",
                loadComponent: () =>
                    import("./areas/parcels/parcel-management-guidance/parcel-management-guidance.component").then((m) => m.ParcelManagementGuidanceComponent),
                canActivate: [UnauthenticatedAccessGuard, ParcelTrackerPowerAccessGuard],
                data: { area: "parcels" },
            },
            {
                path: "reports/inactive-parcels",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Inactive Parcels Report",
                loadComponent: () => import("./areas/parcels/inactive-parcels-report/inactive-parcels-report.component").then((m) => m.InactiveParcelsReportComponent),
                data: { area: "parcels" },
            },
            {
                path: "parcel-permits",
                canActivate: [ParcelPermitsAccessGuard],
                title: "Parcel Permits",
                loadComponent: () => import("./areas/parcels/parcel-permits/parcel-permits.component").then((m) => m.ParcelPermitsComponent),
                data: { area: "parcels" },
            },
            {
                path: "parcel-permits/new/:parcelID",
                canActivate: [UnauthenticatedAccessGuard],
                title: "New Parcel Permit",
                loadComponent: () =>
                    import("./areas/parcels/parcel-permits/parcel-permit-workflow/parcel-permit-workflow-outlet/parcel-permit-workflow-outlet.component").then(
                        (m) => m.ParcelPermitWorkflowOutletComponent
                    ),
                data: { area: "parcels" },
                children: [
                    { path: "", redirectTo: "instructions", pathMatch: "full" },
                    {
                        path: "instructions",
                        loadComponent: () =>
                            import("./areas/parcels/parcel-permits/parcel-permit-workflow/instructions/instructions.component").then((m) => m.InstructionsComponent),
                    },
                ],
            },
            {
                path: "parcel-permits/:parcelPermitID",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Parcel Permit Detail",
                loadComponent: () => import("./areas/parcels/parcel-permits/parcel-permit-detail/parcel-permit-detail.component").then((m) => m.ParcelPermitDetailComponent),
                data: { area: "parcels" },
            },
            {
                path: "parcel-permits/:parcelPermitID/edit",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Edit Parcel Permit",
                loadComponent: () =>
                    import("./areas/parcels/parcel-permits/parcel-permit-workflow/parcel-permit-workflow-outlet/parcel-permit-workflow-outlet.component").then(
                        (m) => m.ParcelPermitWorkflowOutletComponent
                    ),
                data: { area: "parcels" },
                children: [
                    { path: "", redirectTo: "instructions", pathMatch: "full" },
                    {
                        path: "instructions",
                        loadComponent: () =>
                            import("./areas/parcels/parcel-permits/parcel-permit-workflow/instructions/instructions.component").then((m) => m.InstructionsComponent),
                    },
                    {
                        path: "basics",
                        loadComponent: () =>
                            import("./areas/parcels/parcel-permits/parcel-permit-workflow/basics/basics.component").then((m) => m.BasicsComponent),
                    },
                    {
                        path: "land-capability",
                        loadComponent: () =>
                            import("./areas/parcels/parcel-permits/parcel-permit-workflow/land-capability/land-capability.component").then((m) => m.LandCapabilityComponent),
                    },
                    {
                        path: "prior-transactions",
                        loadComponent: () =>
                            import("./areas/parcels/parcel-permits/parcel-permit-workflow/prior-transactions/prior-transactions.component").then(
                                (m) => m.PriorTransactionsComponent
                            ),
                    },
                    {
                        path: "existing-inventory",
                        loadComponent: () =>
                            import("./areas/parcels/parcel-permits/parcel-permit-workflow/existing-inventory/existing-inventory.component").then(
                                (m) => m.ExistingInventoryComponent
                            ),
                    },
                    {
                        path: "exemptions-and-existing-coverage",
                        loadComponent: () =>
                            import(
                                "./areas/parcels/parcel-permits/parcel-permit-workflow/exemptions-and-existing-coverage/exemptions-and-existing-coverage.component"
                            ).then((m) => m.ExemptionsAndExistingCoverageComponent),
                    },
                    {
                        path: "application-overview",
                        canActivate: [EnterPermitDetailsPrerequisiteGuard],
                        loadComponent: () =>
                            import("./areas/parcels/parcel-permits/parcel-permit-workflow/application-overview/application-overview.component").then(
                                (m) => m.ApplicationOverviewComponent
                            ),
                    },
                    {
                        path: "proposed-coverage",
                        canActivate: [EnterPermitDetailsPrerequisiteGuard],
                        loadComponent: () =>
                            import("./areas/parcels/parcel-permits/parcel-permit-workflow/proposed-coverage/proposed-coverage.component").then(
                                (m) => m.ProposedCoverageComponent
                            ),
                    },
                    {
                        path: "proposed-development-rights",
                        canActivate: [EnterPermitDetailsPrerequisiteGuard],
                        loadComponent: () =>
                            import("./areas/parcels/parcel-permits/parcel-permit-workflow/proposed-development-rights/proposed-development-rights.component").then(
                                (m) => m.ProposedDevelopmentRightsComponent
                            ),
                    },
                    {
                        path: "deed-restrictions-and-violations",
                        canActivate: [EnterPermitDetailsPrerequisiteGuard],
                        loadComponent: () =>
                            import(
                                "./areas/parcels/parcel-permits/parcel-permit-workflow/deed-restrictions-and-violations/deed-restrictions-and-violations.component"
                            ).then((m) => m.DeedRestrictionsAndViolationsComponent),
                    },
                    {
                        path: "fees-and-security",
                        canActivate: [EnterPermitDetailsPrerequisiteGuard],
                        loadComponent: () =>
                            import("./areas/parcels/parcel-permits/parcel-permit-workflow/fees-and-security/fees-and-security.component").then(
                                (m) => m.FeesAndSecurityComponent
                            ),
                    },
                    {
                        path: "upload-documents",
                        canActivate: [EnterPermitDetailsPrerequisiteGuard],
                        loadComponent: () =>
                            import("./areas/parcels/parcel-permits/parcel-permit-workflow/upload-documents/upload-documents.component").then(
                                (m) => m.UploadDocumentsComponent
                            ),
                    },
                ],
            },
            {
                path: "development-right-transactions",
                title: "Development Right Transactions",
                loadComponent: () => import("./areas/parcels/tdr-transactions/development-right-transactions.component").then((m) => m.DevelopmentRightTransactionsComponent),
                data: { area: "parcels" },
            },
            {
                path: "tdr-transactions/new-transfer",
                canActivate: [UnauthenticatedAccessGuard],
                title: "New Transfer",
                loadComponent: () => import("./areas/parcels/tdr-transactions/edit-transfer/edit-transfer.component").then((m) => m.EditTransferComponent),
                data: { area: "parcels" },
            },
            {
                path: "tdr-transactions/new-conversion",
                canActivate: [UnauthenticatedAccessGuard],
                title: "New Conversion",
                loadComponent: () => import("./areas/parcels/tdr-transactions/edit-conversion/edit-conversion.component").then((m) => m.EditConversionComponent),
                data: { area: "parcels" },
            },
            {
                path: "tdr-transactions/new-conversion-with-transfer",
                canActivate: [UnauthenticatedAccessGuard],
                title: "New Conversion with Transfer",
                loadComponent: () => import("./areas/parcels/tdr-transactions/edit-conversion-with-transfer/edit-conversion-with-transfer.component").then((m) => m.EditConversionWithTransferComponent),
                data: { area: "parcels" },
            },
            {
                path: "tdr-transactions/new-ecm-retirement",
                canActivate: [UnauthenticatedAccessGuard],
                title: "New ECM Retirement",
                loadComponent: () => import("./areas/parcels/tdr-transactions/edit-ecm-retirement/edit-ecm-retirement.component").then((m) => m.EditECMRetirementComponent),
                data: { area: "parcels" },
            },
            {
                path: "tdr-transactions/new-land-bank-acquisition",
                canActivate: [UnauthenticatedAccessGuard],
                title: "New Land Bank Acquisition",
                loadComponent: () => import("./areas/parcels/tdr-transactions/edit-land-bank-acquisition/edit-land-bank-acquisition.component").then((m) => m.EditLandBankAcquisitionComponent),
                data: { area: "parcels" },
            },
            {
                path: "tdr-transactions/new-allocation",
                canActivate: [UnauthenticatedAccessGuard],
                title: "New Allocation",
                loadComponent: () => import("./areas/parcels/tdr-transactions/edit-allocation/edit-allocation.component").then((m) => m.EditAllocationComponent),
                data: { area: "parcels" },
            },
            {
                path: "tdr-transactions/new-allocation-assignment",
                canActivate: [UnauthenticatedAccessGuard],
                title: "New Allocation Assignment",
                loadComponent: () => import("./areas/parcels/tdr-transactions/edit-allocation-assignment/edit-allocation-assignment.component").then((m) => m.EditAllocationAssignmentComponent),
                data: { area: "parcels" },
            },
            {
                path: "tdr-transactions/new-land-bank-transfer",
                canActivate: [UnauthenticatedAccessGuard],
                title: "New Land Bank Transfer",
                loadComponent: () => import("./areas/parcels/tdr-transactions/edit-land-bank-transfer/edit-land-bank-transfer.component").then((m) => m.EditLandBankTransferComponent),
                data: { area: "parcels" },
            },
            {
                path: "tdr-transactions/new-shorezone-allocation",
                canActivate: [UnauthenticatedAccessGuard],
                title: "New Shorezone Allocation",
                loadComponent: () => import("./areas/parcels/tdr-transactions/edit-shorezone-allocation/edit-shorezone-allocation.component").then((m) => m.EditShorezoneAllocationComponent),
                data: { area: "parcels" },
            },
            {
                path: "tdr-transactions/:tdrTransactionID",
                canActivate: [UnauthenticatedAccessGuard],
                title: "TDR Transaction Detail",
                loadComponent: () => import("./areas/parcels/tdr-transactions/tdr-transaction-detail/tdr-transaction-detail.component").then((m) => m.TdrTransactionDetailComponent),
                data: { area: "parcels" },
            },
            {
                path: "tdr-transactions/:tdrTransactionID/edit-transfer",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Edit Transfer",
                loadComponent: () => import("./areas/parcels/tdr-transactions/edit-transfer/edit-transfer.component").then((m) => m.EditTransferComponent),
                data: { area: "parcels" },
            },
            {
                path: "tdr-transactions/:tdrTransactionID/edit-conversion",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Edit Conversion",
                loadComponent: () => import("./areas/parcels/tdr-transactions/edit-conversion/edit-conversion.component").then((m) => m.EditConversionComponent),
                data: { area: "parcels" },
            },
            {
                path: "tdr-transactions/:tdrTransactionID/edit-conversion-with-transfer",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Edit Conversion with Transfer",
                loadComponent: () => import("./areas/parcels/tdr-transactions/edit-conversion-with-transfer/edit-conversion-with-transfer.component").then((m) => m.EditConversionWithTransferComponent),
                data: { area: "parcels" },
            },
            {
                path: "tdr-transactions/:tdrTransactionID/edit-ecm-retirement",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Edit ECM Retirement",
                loadComponent: () => import("./areas/parcels/tdr-transactions/edit-ecm-retirement/edit-ecm-retirement.component").then((m) => m.EditECMRetirementComponent),
                data: { area: "parcels" },
            },
            {
                path: "tdr-transactions/:tdrTransactionID/edit-land-bank-acquisition",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Edit Land Bank Acquisition",
                loadComponent: () => import("./areas/parcels/tdr-transactions/edit-land-bank-acquisition/edit-land-bank-acquisition.component").then((m) => m.EditLandBankAcquisitionComponent),
                data: { area: "parcels" },
            },
            {
                path: "tdr-transactions/:tdrTransactionID/edit-allocation",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Edit Allocation",
                loadComponent: () => import("./areas/parcels/tdr-transactions/edit-allocation/edit-allocation.component").then((m) => m.EditAllocationComponent),
                data: { area: "parcels" },
            },
            {
                path: "tdr-transactions/:tdrTransactionID/edit-allocation-assignment",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Edit Allocation Assignment",
                loadComponent: () => import("./areas/parcels/tdr-transactions/edit-allocation-assignment/edit-allocation-assignment.component").then((m) => m.EditAllocationAssignmentComponent),
                data: { area: "parcels" },
            },
            {
                path: "tdr-transactions/:tdrTransactionID/edit-shorezone-allocation",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Edit Shorezone Allocation",
                loadComponent: () => import("./areas/parcels/tdr-transactions/edit-shorezone-allocation/edit-shorezone-allocation.component").then((m) => m.EditShorezoneAllocationComponent),
                data: { area: "parcels" },
            },
            {
                path: "tdr-transactions/:tdrTransactionID/edit-land-bank-transfer",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Edit Land Bank Transfer",
                loadComponent: () => import("./areas/parcels/tdr-transactions/edit-land-bank-transfer/edit-land-bank-transfer.component").then((m) => m.EditLandBankTransferComponent),
                data: { area: "parcels" },
            },
            {
                path: "development-right-pools",
                title: "Development Right Pools",
                loadComponent: () => import("./areas/parcels/development-right-pools/development-right-pools.component").then((m) => m.DevelopmentRightPoolsComponent),
                data: { area: "parcels" },
            },
            {
                path: "manage/commodity-pools/:commodityPoolID",
                title: "Development Right Pool Detail",
                loadComponent: () => import("./areas/parcels/development-right-pools/commodity-pool-detail/commodity-pool-detail.component").then((m) => m.CommodityPoolDetailComponent),
                data: { area: "parcels" },
            },
            {
                path: "results/banked-development-rights",
                title: "Banked Development Rights",
                loadComponent: () => import("./areas/parcels/banked-development-rights/banked-development-rights.component").then((m) => m.BankedDevelopmentRightsComponent),
                data: { area: "parcels" },
            },
            {
                path: "manage/lead-agencies",
                title: "Lead Agencies",
                loadComponent: () => import("./areas/parcels/lead-agencies/lead-agencies.component").then((m) => m.LeadAgenciesComponent),
                canActivate: [UnauthenticatedAccessGuard, ParcelTrackerAdminAccessGuard],
                data: { area: "parcels" },
            },
            {
                path: "manage/lead-agency-responsibility-matrix",
                title: "Lead Agency Responsibility Matrix",
                loadComponent: () =>
                    import("./areas/parcels/lead-agency-responsibility-matrix/lead-agency-responsibility-matrix.component").then(
                        (m) => m.LeadAgencyResponsibilityMatrixComponent
                    ),
                data: { area: "parcels" },
            },
            {
                path: "results/ipes-scores",
                title: "All Parcel IPES Scores",
                loadComponent: () => import("./areas/parcels/parcels-by-ipes-scores/parcels-by-ipes-scores.component").then((m) => m.ParcelsByIpesScoresComponent),
                canActivate: [UnauthenticatedAccessGuard, ParcelTrackerPowerAccessGuard],
                data: { area: "parcels" },
            },
            {
                path: "results/ipes-scores/:ipesScoreID",
                title: "IPES Score Summary",
                loadComponent: () =>
                    import("./areas/parcels/ipes-score-detail/ipes-score-detail.component").then((m) => m.IpesScoreDetailComponent),
                data: { area: "parcels" },
            },
            {
                path: "results/parcels-by-land-capability",
                title: "Parcels by Land Capability",
                loadComponent: () => import("./areas/parcels/parcels-by-land-capability/parcels-by-land-capability.component").then((m) => m.ParcelsByLandCapabilityComponent),
                canActivate: [UnauthenticatedAccessGuard, ParcelTrackerPrivilegedViewAccessGuard],
                data: { area: "parcels" },
            },
            {
                path: "results/pool-balance",
                title: "Development Right Pool Balance Report",
                loadComponent: () => import("./areas/parcels/development-right-pools/pool-balance/pool-balance.component").then((m) => m.PoolBalanceComponent),
                data: { area: "parcels" },
            },
            {
                path: "results/deed-restrictions",
                title: "Deed Restrictions Report",
                loadComponent: () => import("./areas/parcels/deed-restrictions/deed-restrictions.component").then((m) => m.DeedRestrictionsComponent),
                canActivate: [UnauthenticatedAccessGuard, ParcelTrackerPrivilegedViewAccessGuard],
                data: { area: "parcels" },
            },
            {
                path: "results/bmp-compliance-report",
                title: "BMP Compliance Report",
                loadComponent: () =>
                    import("./areas/parcels/bmp-compliance-reports/bmp-compliance-report-public/bmp-compliance-report-public.component").then(
                        (m) => m.BMPComplianceReportPublicComponent
                    ),
                canActivate: [UnauthenticatedAccessGuard, ParcelTrackerPrivilegedViewAccessGuard],
                data: { area: "parcels" },
            },
            {
                path: "results/development-rights-inventory",
                title: "Parcels by Current Development Right Inventory",
                loadComponent: () =>
                    import("./areas/parcels/development-rights-inventory/development-rights-inventory.component").then(
                        (m) => m.DevelopmentRightsInventoryComponent
                    ),
                canActivate: [UnauthenticatedAccessGuard, ParcelTrackerPrivilegedViewAccessGuard],
                data: { area: "parcels" },
            },
            {
                path: "results/shorezone-allocations-report",
                title: "Shorezone Allocations Report",
                loadComponent: () =>
                    import("./areas/parcels/shorezone-allocations-report/shorezone-allocations-report.component").then(
                        (m) => m.ShorezoneAllocationsReportComponent
                    ),
                data: { area: "parcels" },
            },
            {
                path: "state-lands-permits",
                title: "State Lands Permits and Leases",
                loadComponent: () => import("./areas/parcels/state-lands-permits/state-lands-permits.component").then((m) => m.StateLandsPermitsComponent),
                data: { area: "parcels" },
            },
            {
                path: "results/accela-cases",
                title: "Accela Cases, Applications, and Permits",
                loadComponent: () => import("./areas/parcels/accela-cases/accela-cases.component").then((m) => m.AccelaCasesComponent),
                data: { area: "parcels" },
            },
            {
                path: "accela-cap-records/:accelaID",
                title: "Accela CAP Record Detail",
                loadComponent: () => import("./areas/parcels/accela-cap-record-detail/accela-cap-record-detail.component").then((m) => m.AccelaCAPRecordDetailComponent),
                data: { area: "parcels" },
            },
            {
                path: "bmp-compliance-reports",
                title: "BMP Compliance Reports",
                loadComponent: () =>
                    import("./areas/parcels/bmp-compliance-reports/bmp-compliance-reports.component").then((m) => m.BMPComplianceReportsComponent),
                canActivate: [UnauthenticatedAccessGuard, ParcelTrackerAdminAccessGuard],
                data: { area: "parcels" },
            },
            {
                // Declared before the :bmpComplianceReportID route so "new" is not captured as an id.
                path: "bmp-compliance-reports/new",
                title: "New BMP Compliance Report",
                loadComponent: () =>
                    import(
                        "./areas/parcels/bmp-compliance-reports/bmp-compliance-report-upsert/bmp-compliance-report-upsert.component"
                    ).then((m) => m.BMPComplianceReportUpsertComponent),
                canActivate: [UnauthenticatedAccessGuard, ParcelTrackerAdminAccessGuard],
                data: { area: "parcels" },
            },
            {
                path: "bmp-compliance-reports/:bmpComplianceReportID",
                title: "BMP Compliance Report Detail",
                loadComponent: () =>
                    import(
                        "./areas/parcels/bmp-compliance-reports/bmp-compliance-report-detail/bmp-compliance-report-detail.component"
                    ).then((m) => m.BMPComplianceReportDetailComponent),
                canActivate: [UnauthenticatedAccessGuard, ParcelTrackerAdminAccessGuard],
                data: { area: "parcels" },
            },
            {
                path: "bmp-compliance-reports/:bmpComplianceReportID/edit",
                title: "Edit BMP Compliance Report",
                loadComponent: () =>
                    import(
                        "./areas/parcels/bmp-compliance-reports/bmp-compliance-report-upsert/bmp-compliance-report-upsert.component"
                    ).then((m) => m.BMPComplianceReportUpsertComponent),
                canActivate: [UnauthenticatedAccessGuard, ParcelTrackerAdminAccessGuard],
                data: { area: "parcels" },
            },
            {
                path: "manage/residential-allocations",
                title: "Residential Allocations",
                loadComponent: () => import("./areas/parcels/residential-allocations/residential-allocations.component").then((m) => m.ResidentialAllocationsComponent),
                canActivate: [UnauthenticatedAccessGuard, ParcelTrackerAdminAccessGuard],
                data: { area: "parcels" },
            },
            {
                // Public read-only detail — no "manage" prefix (viewers aren't managing the RA number).
                // The management list stays at /manage/residential-allocations (admin-only, above).
                path: "residential-allocations/:residentialAllocationNumber",
                title: "Residential Allocation Detail",
                loadComponent: () =>
                    import("./areas/parcels/residential-allocations/residential-allocation-detail/residential-allocation-detail.component").then(
                        (m) => m.ResidentialAllocationDetailComponent
                    ),
                data: { area: "parcels" },
            },
            {
                // Back-compat: redirect the old manage-prefixed detail URL to the new public route,
                // substituting the RA number via the shared LegacyRedirectComponent (a plain string
                // redirectTo cannot substitute :params).
                path: "manage/residential-allocations/:residentialAllocationNumber",
                data: { redirectTo: "/residential-allocations/:residentialAllocationNumber" },
                loadComponent: () => import("./pages/legacy-redirect/legacy-redirect.component").then((m) => m.LegacyRedirectComponent),
            },
            {
                path: "shorezone-allocations/:shorezoneAllocationNumber",
                title: "Shorezone Allocation Detail",
                loadComponent: () =>
                    import("./areas/parcels/shorezone-allocation-detail/shorezone-allocation-detail.component").then(
                        (m) => m.ShorezoneAllocationDetailComponent
                    ),
                data: { area: "parcels" },
            },
            {
                path: "moorings/receipts",
                title: "Receipts",
                loadComponent: () => import("./areas/parcels/mooring-receipts/mooring-receipts.component").then((m) => m.MooringReceiptsComponent),
                data: { area: "parcels" },
            },
            {
                path: "moorings/prior-permits",
                title: "Prior Permits & Payments",
                loadComponent: () => import("./areas/parcels/mooring-prior-permits/mooring-prior-permits.component").then((m) => m.MooringPriorPermitsComponent),
                data: { area: "parcels" },
            },
            {
                path: "moorings/lottery-periods",
                title: "Lottery Periods",
                loadComponent: () => import("./areas/parcels/mooring-lottery-periods/mooring-lottery-periods.component").then((m) => m.MooringLotteryPeriodsComponent),
                data: { area: "parcels" },
            },
            {
                path: "moorings/fees",
                title: "Mooring Fees",
                loadComponent: () => import("./areas/parcels/mooring-fees/mooring-fees.component").then((m) => m.MooringFeesComponent),
                data: { area: "parcels" },
            },
            {
                // Bare /moorings (e.g. from notification email links) redirects to the
                // mooring home page. pathMatch: "full" so only the exact "/moorings" URL
                // matches — every "moorings/*" child route below is unaffected.
                path: "moorings",
                pathMatch: "full",
                redirectTo: "moorings/home",
                data: { area: "parcels" },
            },
            {
                path: "moorings/home",
                title: "Moorings",
                loadComponent: () => import("./areas/parcels/mooring-home/mooring-home.component").then((m) => m.MooringHomeComponent),
                data: { area: "parcels" },
            },
            {
                path: "moorings/inventory",
                title: "Mooring Inventory",
                loadComponent: () => import("./areas/parcels/mooring-inventory/mooring-inventory.component").then((m) => m.MooringInventoryComponent),
                data: { area: "parcels" },
            },
            {
                path: "moorings/inventory/new",
                title: "Add a Missing Mooring",
                canActivate: [UnauthenticatedAccessGuard, ParcelTrackerPowerAccessGuard],
                loadComponent: () => import("./areas/parcels/mooring-inventory/new-mooring/new-mooring.component").then((m) => m.NewMooringComponent),
                data: { area: "parcels" },
            },
            {
                path: "moorings/tag-search",
                title: "Search for a Mooring Tag",
                loadComponent: () =>
                    import("./areas/parcels/mooring-tag-search/mooring-tag-search.component").then(
                        (m) => m.MooringTagSearchComponent
                    ),
                data: { area: "parcels" },
            },
            {
                path: "moorings/tags",
                title: "Tags",
                loadComponent: () => import("./areas/parcels/mooring-tags/mooring-tags.component").then((m) => m.MooringTagsComponent),
                data: { area: "parcels" },
            },
            {
                path: "moorings/invoice-audits",
                title: "Mooring Invoice Audits",
                loadComponent: () => import("./areas/parcels/mooring-invoice-audits/mooring-invoice-audits.component").then((m) => m.MooringInvoiceAuditsComponent),
                data: { area: "parcels" },
            },
            {
                path: "moorings/pay-fees",
                title: "Pay Mooring Fees",
                loadComponent: () =>
                    import("./areas/parcels/mooring-pay-fees/mooring-pay-fees.component").then((m) => m.MooringPayFeesComponent),
                data: { area: "parcels" },
            },
            {
                path: "moorings/my-invoices",
                title: "My Invoices",
                canActivate: [UnauthenticatedAccessGuard],
                loadComponent: () =>
                    import("./areas/parcels/mooring-my-invoices/mooring-my-invoices.component").then(
                        (m) => m.MooringMyInvoicesComponent
                    ),
                data: { area: "parcels" },
            },
            {
                path: "moorings/invoices/:invoiceID/payment",
                title: "Invoice Payment",
                canActivate: [UnauthenticatedAccessGuard],
                loadComponent: () => import("./areas/parcels/mooring-invoice-payment/mooring-invoice-payment.component").then((m) => m.MooringInvoicePaymentComponent),
                data: { area: "parcels" },
            },
            {
                path: "moorings/permit-invoices/:invoiceID/payment",
                title: "Permit Invoice Payment",
                canActivate: [UnauthenticatedAccessGuard],
                loadComponent: () => import("./areas/parcels/mooring-permit-invoice-payment/mooring-permit-invoice-payment.component").then((m) => m.MooringPermitInvoicePaymentComponent),
                data: { area: "parcels" },
            },
            {
                path: "moorings/lottery-invoices/:invoiceID/payment",
                title: "Lottery Invoice Payment",
                canActivate: [UnauthenticatedAccessGuard],
                loadComponent: () => import("./areas/parcels/mooring-lottery-invoice-payment/mooring-lottery-invoice-payment.component").then((m) => m.MooringLotteryInvoicePaymentComponent),
                data: { area: "parcels" },
            },
            {
                path: "moorings/invoices",
                title: "Mooring Invoices",
                loadComponent: () => import("./areas/parcels/mooring-invoices/mooring-invoices.component").then((m) => m.MooringInvoicesComponent),
                data: { area: "parcels" },
            },
            {
                path: "moorings/financial-reports",
                title: "Financial Reports",
                loadComponent: () => import("./areas/parcels/mooring-financial-reports/mooring-financial-reports.component").then((m) => m.MooringFinancialReportsComponent),
                data: { area: "parcels" },
            },
            {
                path: "moorings/registration-dashboard",
                title: "Registration Dashboard",
                canActivate: [UnauthenticatedAccessGuard],
                loadComponent: () => import("./areas/parcels/mooring-registration-dashboard/mooring-registration-dashboard.component").then((m) => m.MooringRegistrationDashboardComponent),
                data: { area: "parcels" },
            },
            {
                path: "moorings/registrations/my-submissions",
                title: "My Registrations",
                canActivate: [UnauthenticatedAccessGuard],
                loadComponent: () =>
                    import("./areas/parcels/mooring-my-registrations/mooring-my-registrations.component").then(
                        (m) => m.MooringMyRegistrationsComponent
                    ),
                data: { area: "parcels" },
            },
            {
                path: "moorings/registrations/:mooringRegistrationSubmissionID",
                title: "Mooring Registration Detail",
                // Public/anonymous users may view the registration detail page (sensitive panels are
                // gated server-side via CallerHasSensitiveView). No auth guard — mirrors legacy
                // [AnonymousUnclassifiedFeature] on the MVC Detail action.
                loadComponent: () =>
                    import("./areas/parcels/mooring-registration-submission-detail/mooring-registration-submission-detail.component").then(
                        (m) => m.MooringRegistrationSubmissionDetailComponent
                    ),
                data: { area: "parcels" },
            },
            {
                path: "moorings/registrations/:mooringRegistrationSubmissionID/review",
                title: "Registration Review",
                canActivate: [UnauthenticatedAccessGuard],
                loadComponent: () =>
                    import("./areas/parcels/mooring-registration-review-detail/mooring-registration-review-detail.component").then(
                        (m) => m.MooringRegistrationReviewDetailComponent
                    ),
                data: { area: "parcels" },
            },
            {
                path: "moorings/renew/:submissionID",
                title: "Renew Mooring Registration",
                canActivate: [UnauthenticatedAccessGuard],
                loadComponent: () =>
                    import("./areas/parcels/mooring-registration-renewal-renew/mooring-registration-renewal-renew.component").then(
                        (m) => m.MooringRegistrationRenewalRenewComponent
                    ),
                data: { area: "parcels" },
            },
            {
                path: "moorings/permit-applications/:mooringPermitApplicationID/review",
                title: "Permit Review",
                canActivate: [UnauthenticatedAccessGuard],
                loadComponent: () =>
                    import("./areas/parcels/mooring-permit-application-review-detail/mooring-permit-application-review-detail.component").then(
                        (m) => m.MooringPermitApplicationReviewDetailComponent
                    ),
                data: { area: "parcels" },
            },
            {
                path: "moorings/permit-applications/:mooringPermitApplicationID/view",
                title: "Mooring Permit",
                canActivate: [UnauthenticatedAccessGuard],
                loadComponent: () =>
                    import("./areas/parcels/mooring-permit-view/mooring-permit-view.component").then(
                        (m) => m.MooringPermitViewComponent
                    ),
                data: { area: "parcels" },
            },
            {
                path: "moorings/registration/new",
                title: "New Mooring Registration",
                canActivate: [UnauthenticatedAccessGuard],
                loadComponent: () =>
                    import("./areas/parcels/mooring-registration-workflow/mooring-registration-workflow-outlet/mooring-registration-workflow-outlet.component").then(
                        (m) => m.MooringRegistrationWorkflowOutletComponent
                    ),
                data: { area: "parcels" },
                children: [
                    { path: "", redirectTo: "applicant-information", pathMatch: "full" },
                    {
                        path: "applicant-information",
                        loadComponent: () =>
                            import("./areas/parcels/mooring-registration-workflow/applicant-information/applicant-information.component").then(
                                (m) => m.ApplicantInformationComponent
                            ),
                    },
                ],
            },
            {
                path: "moorings/registration/edit/:mooringRegistrationSubmissionID",
                title: "Edit Mooring Registration",
                canActivate: [UnauthenticatedAccessGuard],
                loadComponent: () =>
                    import("./areas/parcels/mooring-registration-workflow/mooring-registration-workflow-outlet/mooring-registration-workflow-outlet.component").then(
                        (m) => m.MooringRegistrationWorkflowOutletComponent
                    ),
                data: { area: "parcels" },
                children: [
                    { path: "", redirectTo: "applicant-information", pathMatch: "full" },
                    {
                        path: "applicant-information",
                        loadComponent: () =>
                            import("./areas/parcels/mooring-registration-workflow/applicant-information/applicant-information.component").then(
                                (m) => m.ApplicantInformationComponent
                            ),
                    },
                    {
                        path: "select-parcels",
                        loadComponent: () =>
                            import("./areas/parcels/mooring-registration-workflow/select-parcels/select-parcels.component").then(
                                (m) => m.SelectParcelsComponent
                            ),
                    },
                    {
                        path: "select-moorings",
                        loadComponent: () =>
                            import("./areas/parcels/mooring-registration-workflow/select-moorings/select-moorings.component").then(
                                (m) => m.SelectMooringsComponent
                            ),
                    },
                    {
                        path: "parcel-owner-information",
                        loadComponent: () =>
                            import("./areas/parcels/mooring-registration-workflow/parcel-owner-information/parcel-owner-information.component").then(
                                (m) => m.ParcelOwnerInformationComponent
                            ),
                    },
                    {
                        path: "upload-documents",
                        loadComponent: () =>
                            import("./areas/parcels/mooring-registration-workflow/upload-documents/upload-documents.component").then(
                                (m) => m.UploadDocumentsComponent
                            ),
                    },
                    {
                        path: "submit-registration",
                        loadComponent: () =>
                            import("./areas/parcels/mooring-registration-workflow/submit-registration/submit-registration.component").then(
                                (m) => m.SubmitRegistrationComponent
                            ),
                    },
                ],
            },
            {
                path: "moorings/lottery-dashboard",
                title: "Lottery Dashboard",
                loadComponent: () => import("./areas/parcels/mooring-lottery-dashboard/mooring-lottery-dashboard.component").then((m) => m.MooringLotteryDashboardComponent),
                data: { area: "parcels" },
            },
            {
                path: "moorings/lottery/all/:year",
                title: "Mooring Lottery Submissions",
                loadComponent: () =>
                    import("./areas/parcels/mooring-all-lottery-submissions/mooring-all-lottery-submissions.component").then(
                        (m) => m.MooringAllLotterySubmissionsComponent
                    ),
                data: { area: "parcels" },
            },
            {
                path: "moorings/lottery/all",
                title: "Mooring Lottery Submissions",
                loadComponent: () =>
                    import("./areas/parcels/mooring-all-lottery-submissions/mooring-all-lottery-submissions.component").then(
                        (m) => m.MooringAllLotterySubmissionsComponent
                    ),
                data: { area: "parcels" },
            },
            {
                path: "moorings/lottery/my-submissions/:year",
                title: "My Lottery Submissions",
                canActivate: [UnauthenticatedAccessGuard],
                loadComponent: () =>
                    import("./areas/parcels/mooring-my-lottery-submissions/mooring-my-lottery-submissions.component").then(
                        (m) => m.MooringMyLotterySubmissionsComponent
                    ),
                data: { area: "parcels" },
            },
            {
                path: "moorings/lottery/my-submissions",
                title: "My Lottery Submissions",
                canActivate: [UnauthenticatedAccessGuard],
                loadComponent: () =>
                    import("./areas/parcels/mooring-my-lottery-submissions/mooring-my-lottery-submissions.component").then(
                        (m) => m.MooringMyLotterySubmissionsComponent
                    ),
                data: { area: "parcels" },
            },
            {
                path: "moorings/lottery/:submissionID/review",
                title: "Lottery Submission Review",
                loadComponent: () =>
                    import("./areas/parcels/mooring-lottery-review-detail/mooring-lottery-review-detail.component").then((m) => m.MooringLotteryReviewDetailComponent),
                data: { area: "parcels" },
            },
            {
                path: "moorings/lottery/new",
                title: "New Mooring Lottery Submission",
                canActivate: [UnauthenticatedAccessGuard],
                loadComponent: () =>
                    import("./areas/parcels/mooring-lottery-submission-workflow/mooring-lottery-submission-workflow-outlet/mooring-lottery-submission-workflow-outlet.component").then(
                        (m) => m.MooringLotterySubmissionWorkflowOutletComponent
                    ),
                data: { area: "parcels" },
                children: [
                    { path: "", redirectTo: "instructions", pathMatch: "full" },
                    {
                        path: "instructions",
                        loadComponent: () =>
                            import("./areas/parcels/mooring-lottery-submission-workflow/instructions/instructions.component").then(
                                (m) => m.InstructionsComponent
                            ),
                    },
                    {
                        path: "applicant-information",
                        loadComponent: () =>
                            import("./areas/parcels/mooring-lottery-submission-workflow/applicant-information/applicant-information.component").then(
                                (m) => m.ApplicantInformationComponent
                            ),
                    },
                ],
            },
            {
                path: "moorings/lottery/edit/:mooringLotterySubmissionID",
                title: "Edit Mooring Lottery Submission",
                canActivate: [UnauthenticatedAccessGuard],
                loadComponent: () =>
                    import("./areas/parcels/mooring-lottery-submission-workflow/mooring-lottery-submission-workflow-outlet/mooring-lottery-submission-workflow-outlet.component").then(
                        (m) => m.MooringLotterySubmissionWorkflowOutletComponent
                    ),
                data: { area: "parcels" },
                children: [
                    { path: "", redirectTo: "instructions", pathMatch: "full" },
                    {
                        path: "instructions",
                        loadComponent: () =>
                            import("./areas/parcels/mooring-lottery-submission-workflow/instructions/instructions.component").then(
                                (m) => m.InstructionsComponent
                            ),
                    },
                    {
                        path: "applicant-information",
                        loadComponent: () =>
                            import("./areas/parcels/mooring-lottery-submission-workflow/applicant-information/applicant-information.component").then(
                                (m) => m.ApplicantInformationComponent
                            ),
                    },
                    {
                        path: "parcel-and-mooring-information",
                        loadComponent: () =>
                            import("./areas/parcels/mooring-lottery-submission-workflow/parcel-and-mooring-information/parcel-and-mooring-information.component").then(
                                (m) => m.ParcelAndMooringInformationComponent
                            ),
                    },
                    {
                        path: "parcel-owner-information",
                        loadComponent: () =>
                            import("./areas/parcels/mooring-lottery-submission-workflow/parcel-owner-information/parcel-owner-information.component").then(
                                (m) => m.ParcelOwnerInformationComponent
                            ),
                    },
                    {
                        path: "upload-documents",
                        loadComponent: () =>
                            import("./areas/parcels/mooring-lottery-submission-workflow/upload-documents/upload-documents.component").then(
                                (m) => m.UploadDocumentsComponent
                            ),
                    },
                    {
                        path: "submit",
                        loadComponent: () =>
                            import("./areas/parcels/mooring-lottery-submission-workflow/submit/submit.component").then(
                                (m) => m.SubmitComponent
                            ),
                    },
                ],
            },
            {
                path: "moorings/notifications",
                title: "Mooring Renewal Notifications",
                loadComponent: () => import("./areas/parcels/mooring-notifications/mooring-notifications.component").then((m) => m.ManageMooringRenewalNotificationsComponent),
                data: { area: "parcels" },
            },
            {
                path: "moorings/tags/:mooringTagID",
                title: "Mooring Tag Detail",
                loadComponent: () =>
                    import("./areas/parcels/mooring-tag-detail/mooring-tag-detail.component").then((m) => m.MooringTagDetailComponent),
                data: { area: "parcels" },
            },
            {
                path: "moorings/:mooringID",
                title: "Mooring Detail",
                loadComponent: () => import("./areas/parcels/mooring-detail/mooring-detail.component").then((m) => m.MooringDetailComponent),
                data: { area: "parcels" },
            },
            {
                path: "contact-us",
                title: "Contact Us",
                loadComponent: () => {
                    const subdomainService = inject(SubdomainService);
                    const subdomain = subdomainService.getSubdomain();
                    switch (subdomain) {
                        case "climate":
                            return import("./areas/climate/about.component").then((m) => m.ClimateAboutComponent);
                        case "clarity":
                            return import("./areas/clarity/contact-us/contact-us.component").then((m) => m.ContactUsComponent);
                        case "monitoring":
                            return import("./areas/monitoring/about.component").then((m) => m.MonitoringAboutComponent);
                        case "parcels":
                            return import("./areas/parcels/about.component").then((m) => m.ParcelsAboutComponent);
                        case "marketplace":
                            return import("./areas/marketplace/about.component").then((m) => m.MarketplaceAboutComponent);
                        case "threshold":
                            return import("./areas/threshold/about.component").then((m) => m.ThresholdAboutComponent);
                        case "transportation":
                            return import("./areas/transportation/about.component").then((m) => m.TransportationAboutComponent);
                        case "ltinfo":
                        case "main":
                            return import("./pages/about/about.component").then((m) => m.AboutComponent);
                        default:
                            return import("./pages/about/about.component").then((m) => m.AboutComponent);
                    }
                },
            },
            {
                path: "data-center",
                title: "Data Center",
                loadComponent: () => import("./pages/data-center/data-center.component").then((m) => m.DataCenterComponent),
                data: { area: "main" },
            },
            {
                path: "climate-resilience-goals-and-outcomes",
                title: "Climate Resilience Goals & Outcomes",
                canActivate: [ClimateResilienceAdminAccessGuard],
                loadComponent: () =>
                    import("./pages/climate-resilience-goals-outcomes/climate-resilience-goals-outcomes.component").then(
                        (m) => m.ClimateResilienceGoalsOutcomesComponent
                    ),
                data: { area: "main" },
            },
            {
                path: "document-library",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Document Library",
                loadComponent: () => import("./pages/document-library/document-library-list.component"),
                data: { area: "main" },
            },
            {
                // Single list route. A specific announcement is focused via the ?announcementID=
                // query param (not a path segment) — it's a highlight/scroll on the list, not a detail page.
                path: "news-announcements",
                title: "News & Announcements",
                loadComponent: () => import("./pages/announcements/announcements.component").then((m) => m.AnnouncementsComponent),
                data: { area: "main" },
            },
            {
                path: "funding-sources",
                title: "Funding Sources",
                loadComponent: () => import("./pages/funding-sources/funding-sources.component").then((m) => m.FundingSourcesComponent),
                data: { area: "main" },
            },
            {
                path: `funding-sources/:${routeParams.fundingSourceID}`,
                title: "Funding Source",
                loadComponent: () => import("./pages/funding-sources/funding-source-detail/funding-source-detail.component").then((m) => m.FundingSourceDetailComponent),
                data: { area: "main" },
            },
            {
                path: "indicators",
                title: "Indicators",
                loadComponent: () => import("./pages/indicators/indicators.component").then((m) => m.IndicatorsComponent),
                data: { area: "main" },
            },
            {
                path: "indicators/threshold-manage",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Manage Threshold Indicators",
                loadComponent: () =>
                    import("./pages/indicators/threshold-indicator-manage/threshold-indicator-manage.component").then(
                        (m) => m.ThresholdIndicatorManageComponent,
                    ),
                data: { area: "main" },
            },
            {
                path: "indicators/:indicatorID",
                title: "Indicator Detail",
                loadComponent: () => import("./pages/indicators/indicator-detail/indicator-detail.component").then((m) => m.IndicatorDetailComponent),
                data: { area: "main" },
            },
            {
                path: "indicators/:indicatorID/threshold-updates/:thresholdIndicatorUpdateBatchID",
                title: "Threshold Indicator Update Detail",
                loadComponent: () =>
                    import("./pages/threshold-indicator-updates/threshold-indicator-update-detail/threshold-indicator-update-detail.component").then(
                        (m) => m.ThresholdIndicatorUpdateDetailComponent
                    ),
                data: { area: "main" },
            },
            {
                path: "local-and-regional-plans",
                title: "Local and Regional Plans",
                loadComponent: () => import("./pages/local-and-regional-plans/local-and-regional-plans.component").then((m) => m.LocalAndRegionalPlansComponent),
                data: { area: "main" },
            },
            {
                path: "local-and-regional-plans/:localAndRegionalPlanID",
                title: "Local and Regional Plan Detail",
                loadComponent: () =>
                    import("./pages/local-and-regional-plans/local-and-regional-plan-detail/local-and-regional-plan-detail.component").then(
                        (m) => m.LocalAndRegionalPlanDetailComponent
                    ),
                data: { area: "main" },
            },
            {
                path: "monitoring-programs",
                title: "Monitoring Programs",
                loadComponent: () =>
                    import("./areas/monitoring/monitoring-programs/monitoring-programs-list.component").then(
                        (m) => m.MonitoringProgramsListComponent
                    ),
                data: { area: "monitoring" },
            },
            {
                path: "monitoring-programs/:monitoringProgramID",
                title: "Monitoring Program Detail",
                loadComponent: () =>
                    import("./areas/monitoring/monitoring-programs/monitoring-program-detail/monitoring-program-detail.component").then(
                        (m) => m.MonitoringProgramDetailComponent
                    ),
                data: { area: "monitoring" },
            },
            {
                path: "monitoring-sites/:monitoringSiteID",
                title: "Monitoring Site Detail",
                loadComponent: () =>
                    import("./areas/monitoring/monitoring-sites/monitoring-site-detail/monitoring-site-detail.component").then(
                        (m) => m.MonitoringSiteDetailComponent
                    ),
                data: { area: "monitoring" },
            },
            {
                path: "monitoring-sites/:monitoringSiteID/edit",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Edit Monitoring Site",
                loadComponent: () =>
                    import("./areas/monitoring/monitoring-sites/monitoring-site-edit/monitoring-site-edit.component").then(
                        (m) => m.MonitoringSiteEditComponent
                    ),
                data: { area: "monitoring" },
            },
            {
                path: "transit-data",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Transit Data Entry",
                loadComponent: () =>
                    import("./areas/monitoring/transit-data/transit-data.component").then(
                        (m) => m.TransitDataComponent
                    ),
                data: { area: "monitoring" },
            },
            {
                path: "monitoring-users",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Monitoring Users",
                loadComponent: () =>
                    import("./areas/monitoring/monitoring-users/monitoring-users-list.component").then(
                        (m) => m.MonitoringUsersListComponent
                    ),
                data: { area: "monitoring" },
            },
            {
                path: "organizations",
                title: "Organizations",
                loadComponent: () => import("./pages/organizations/organizations.component").then((m) => m.OrganizationsComponent),
                data: { area: "main" },
            },
            {
                path: `organizations/:${routeParams.organizationID}`,
                title: "Organization",
                loadComponent: () => import("./pages/organizations/organization-detail/organization-detail.component").then((m) => m.OrganizationDetailComponent),
                data: { area: "main" },
            },
            {
                path: "web-services",
                title: "Web Services",
                loadComponent: () => import("./pages/web-services/web-services.component").then((m) => m.WebServicesComponent),
                data: { area: "main" },
            },
            // NRAP (Nearshore Resource Allocation Program) routes
            {
                path: "initiative/nrap",
                title: "Nearshore Resource Allocation Program",
                loadComponent: () => import("./areas/nrap/nrap-home.component").then((m) => m.NRAPHomeComponent),
                data: { area: "main" },
            },
            {
                path: "initiative/nrap/focus-areas/:nrapFocusAreaID",
                title: "NRAP Focus Area",
                loadComponent: () =>
                    import("./areas/nrap/nrap-focus-area-detail/nrap-focus-area-detail.component").then((m) => m.NRAPFocusAreaDetailComponent),
                data: { area: "main" },
            },
            {
                path: "initiative/nrap/findings/:nrapFindingID",
                title: "NRAP Finding",
                loadComponent: () =>
                    import("./areas/nrap/nrap-finding-detail/nrap-finding-detail.component").then((m) => m.NRAPFindingDetailComponent),
                data: { area: "main" },
            },

            {
                path: "watersheds",
                loadComponent: () => import("./pages/watersheds/watersheds.component").then((m) => m.WatershedsComponent),
                data: { area: "main" },
            },
            {
                path: "watersheds/:watershedID",
                title: "Watershed Detail",
                loadComponent: () => import("./pages/watersheds/watershed-detail/watershed-detail.component").then((m) => m.WatershedDetailComponent),
                data: { area: "main" },
            },
            // Transportation area-specific routes
            {
                path: "transportation-strategies",
                title: "Transportation Strategies",
                loadComponent: () =>
                    import("./areas/transportation/transportation-strategies/transportation-strategies-list.component").then(
                        (m) => m.TransportationStrategiesListComponent
                    ),
                data: { area: "transportation" },
            },
            {
                path: "transportation-strategies/:transportationStrategyID",
                title: "Transportation Strategy Detail",
                loadComponent: () =>
                    import("./areas/transportation/transportation-strategies/transportation-strategy-detail/transportation-strategy-detail.component").then(
                        (m) => m.TransportationStrategyDetailComponent
                    ),
                data: { area: "transportation" },
            },
            {
                path: "transportation-objectives",
                title: "Transportation Objectives",
                loadComponent: () =>
                    import("./areas/transportation/transportation-objectives/transportation-objectives-list.component").then(
                        (m) => m.TransportationObjectivesListComponent
                    ),
                data: { area: "transportation" },
            },
            {
                path: "transportation-objectives/:transportationObjectiveID",
                title: "Transportation Objective Detail",
                loadComponent: () =>
                    import("./areas/transportation/transportation-objectives/transportation-objective-detail/transportation-objective-detail.component").then(
                        (m) => m.TransportationObjectiveDetailComponent
                    ),
                data: { area: "transportation" },
            },
            {
                path: "transportation-projects",
                title: "Transportation Projects",
                loadComponent: () =>
                    import("./areas/transportation/transportation-projects/transportation-projects-list.component").then(
                        (m) => m.TransportationProjectsListComponent
                    ),
                data: { area: "transportation" },
            },
            {
                path: "transportation-projects/featured",
                title: "Featured Transportation Projects",
                loadComponent: () =>
                    import("./areas/transportation/featured-projects/featured-projects.component").then((m) => m.FeaturedProjectsComponent),
                data: { area: "transportation" },
            },
            {
                path: "transportation-projects/:transportationProjectID/budgets",
                title: "Edit Project Budget",
                loadComponent: () =>
                    import("./areas/transportation/transportation-projects/transportation-project-budget-editor/transportation-project-budget-editor.component").then(
                        (m) => m.TransportationProjectBudgetEditorComponent
                    ),
                canActivate: [UnauthenticatedAccessGuard, TransportationAdminAccessGuard],
                data: { area: "transportation" },
            },
            {
                path: "transportation/funding-dashboard",
                title: "Funding Dashboard",
                loadComponent: () =>
                    import("./areas/transportation/funding-dashboard/funding-dashboard.component").then(
                        (m) => m.FundingDashboardComponent
                    ),
                data: { area: "transportation" },
            },
            {
                path: "transportation/data-trends",
                title: "Data Trends",
                loadComponent: () =>
                    import("./areas/transportation/data-trends/data-trends-list.component").then(
                        (m) => m.DataTrendsListComponent
                    ),
                data: { area: "transportation" },
            },
            {
                path: "transportation/data-trends/:transportationDataTrendID",
                title: "Data Trend Detail",
                loadComponent: () =>
                    import("./areas/transportation/data-trends/data-trend-detail/data-trend-detail.component").then(
                        (m) => m.DataTrendDetailComponent
                    ),
                data: { area: "transportation" },
            },
            {
                path: "transportation-taxonomy",
                title: "Transportation Taxonomy",
                loadComponent: () =>
                    import("./areas/transportation/program-info/transportation-taxonomy.component").then(
                        (m) => m.TransportationTaxonomyComponent
                    ),
                data: { area: "transportation" },
            },
            {
                path: "manage/assessment",
                title: "Manage Transportation Assessment",
                loadComponent: () =>
                    import("./areas/transportation/manage/transportation-assessment-manage/transportation-assessment-manage.component").then(
                        (m) => m.TransportationAssessmentManageComponent
                    ),
                canActivate: [UnauthenticatedAccessGuard, TransportationAdminAccessGuard],
                data: { area: "transportation" },
            },
            {
                path: "manage/cost-parameters",
                title: "Transportation Cost Parameters",
                loadComponent: () =>
                    import("./areas/transportation/manage/transportation-cost-parameters/transportation-cost-parameters.component").then(
                        (m) => m.TransportationCostParametersComponent
                    ),
                canActivate: [UnauthenticatedAccessGuard, TransportationAdminAccessGuard],
                data: { area: "transportation" },
            },
            {
                path: "transportation/regional-vision",
                title: "Regional Vision",
                loadComponent: () =>
                    import("./areas/transportation/regional-vision/regional-vision.component").then(
                        (m) => m.RegionalVisionComponent
                    ),
                data: { area: "transportation" },
            },
            // Threshold area-specific routes
            {
                path: "threshold",
                title: "Evaluation Overview",
                loadComponent: () =>
                    import("./areas/threshold/evaluation-overview/evaluation-overview.component").then(
                        (m) => m.EvaluationOverviewComponent
                    ),
                data: { area: "threshold" },
            },
            {
                path: "threshold/search",
                title: "Search Threshold Indicators",
                loadComponent: () =>
                    import("./areas/threshold/threshold-indicator-search/threshold-indicator-search.component").then(
                        (m) => m.ThresholdIndicatorSearchComponent
                    ),
                data: { area: "threshold" },
            },
            {
                path: `threshold/categories/:${routeParams.thresholdCategoryID}`,
                title: "Threshold Category Detail",
                loadComponent: () =>
                    import("./areas/threshold/threshold-category-detail/threshold-category-detail.component").then(
                        (m) => m.ThresholdCategoryDetailComponent
                    ),
                data: { area: "threshold" },
            },
            {
                path: `threshold/reporting-categories/:${routeParams.thresholdReportingCategoryID}`,
                title: "Threshold Reporting Category Detail",
                loadComponent: () =>
                    import("./areas/threshold/threshold-reporting-category-detail/threshold-reporting-category-detail.component").then(
                        (m) => m.ThresholdReportingCategoryDetailComponent
                    ),
                data: { area: "threshold" },
            },
            {
                path: `threshold/indicators/:${routeParams.thresholdIndicatorID}`,
                title: "Threshold Indicator Detail",
                loadComponent: () =>
                    import("./areas/threshold/threshold-indicator-detail/threshold-indicator-detail.component").then(
                        (m) => m.ThresholdIndicatorDetailComponent
                    ),
                data: { area: "threshold" },
            },
            {
                path: "threshold/evaluations",
                title: "Threshold Evaluation Periods",
                loadComponent: () =>
                    import("./areas/threshold/threshold-evaluation-period-list/threshold-evaluation-period-list.component").then(
                        (m) => m.ThresholdEvaluationPeriodListComponent
                    ),
                data: { area: "threshold" },
            },
            {
                path: `threshold/evaluations/:${routeParams.thresholdEvaluationPeriodID}`,
                title: "Threshold Evaluation Executive Summary",
                loadComponent: () =>
                    import("./areas/threshold/threshold-evaluation-period-detail/threshold-evaluation-period-detail.component").then(
                        (m) => m.ThresholdEvaluationPeriodDetailComponent
                    ),
                data: { area: "threshold" },
            },
            {
                path: `threshold/past-evaluations/:${routeParams.thresholdEvaluationPeriodID}`,
                title: "Past Threshold Evaluation",
                loadComponent: () =>
                    import("./areas/threshold/past-evaluation-detail/past-evaluation-detail.component").then(
                        (m) => m.PastEvaluationDetailComponent
                    ),
                data: { area: "threshold" },
            },
            {
                path: "threshold/cumulative-accounting",
                title: "Cumulative Accounting",
                loadComponent: () =>
                    import("./areas/threshold/cumulative-accounting/cumulative-accounting.component").then(
                        (m) => m.CumulativeAccountingComponent
                    ),
                data: { area: "threshold" },
            },
            {
                path: `threshold/cumulative-accounting/:${routeParams.thresholdEvaluationPeriodID}`,
                title: "Cumulative Accounting",
                loadComponent: () =>
                    import("./areas/threshold/cumulative-accounting/cumulative-accounting.component").then(
                        (m) => m.CumulativeAccountingComponent
                    ),
                data: { area: "threshold" },
            },
            {
                path: "threshold/compliance-measures",
                title: "Compliance Measures",
                loadComponent: () =>
                    import("./areas/threshold/compliance-measure/compliance-measure.component").then(
                        (m) => m.ComplianceMeasureComponent
                    ),
                data: { area: "threshold" },
            },
            {
                path: `threshold/compliance-measures/:${routeParams.thresholdEvaluationPeriodID}`,
                title: "Compliance Measures",
                loadComponent: () =>
                    import("./areas/threshold/compliance-measure/compliance-measure.component").then(
                        (m) => m.ComplianceMeasureComponent
                    ),
                data: { area: "threshold" },
            },
            {
                path: "threshold/about-the-indicators",
                title: "About the Thresholds",
                loadComponent: () =>
                    import("./areas/threshold/about.component").then(
                        (m) => m.ThresholdAboutComponent
                    ),
                data: { area: "threshold", pageType: "about-the-indicators" },
            },
            {
                path: "threshold/adaptive-management",
                title: "Adaptive Management",
                loadComponent: () =>
                    import("./areas/threshold/about.component").then(
                        (m) => m.ThresholdAboutComponent
                    ),
                data: { area: "threshold", pageType: "adaptive-management" },
            },
            {
                path: "threshold/how-thresholds-are-evaluated",
                title: "How Thresholds are Evaluated",
                loadComponent: () =>
                    import("./areas/threshold/about.component").then(
                        (m) => m.ThresholdAboutComponent
                    ),
                data: { area: "threshold", pageType: "how-thresholds-are-evaluated" },
            },
            // EIP area-specific routes (no subdomain switch needed)
            {
                path: "eip-overview",
                title: "EIP Overview",
                loadComponent: () => import("./areas/eip/about/eip-overview.component").then((m) => m.EipOverviewComponent),
                data: { area: "eip" },
            },
            {
                path: "eip-partners",
                title: "EIP Partners",
                loadComponent: () => import("./areas/eip/about/eip-partners.component").then((m) => m.EipPartnersComponent),
                data: { area: "eip" },
            },
            {
                path: "this-tool",
                title: "This Tool",
                loadComponent: () => import("./areas/eip/about/this-tool.component").then((m) => m.ThisToolComponent),
                data: { area: "eip" },
            },
            {
                path: "eip-faq",
                title: "EIP FAQ",
                loadComponent: () => import("./areas/eip/about/eip-faq.component").then((m) => m.EipFaqComponent),
                data: { area: "eip" },
            },
            {
                path: "demo-script",
                title: "Demo Script",
                loadComponent: () => import("./areas/eip/about/demo-script.component").then((m) => m.DemoScriptComponent),
                data: { area: "eip" },
            },
            {
                path: "annual-approval-process",
                title: "Annual Approval Process",
                loadComponent: () => import("./areas/eip/about/annual-approval-process.component").then((m) => m.AnnualApprovalProcessComponent),
                data: { area: "eip" },
            },
            {
                path: "projects/map",
                title: "EIP Project Map",
                loadComponent: () => import("./areas/eip/eip-project-map/eip-project-map.component").then((m) => m.EipProjectMapComponent),
                data: { area: "eip" },
            },
            {
                path: `projects/fact-sheet/:${routeParams.projectNumber}`,
                title: "Project Fact Sheet",
                loadComponent: () => import("./areas/eip/project-fact-sheet/project-fact-sheet.component").then((m) => m.ProjectFactSheetComponent),
                data: { area: "eip" },
            },
            // File-resource links inside DB rich-text content are stored environment-agnostic
            // ("/file-resources/{guid}"), but the API serves them from a different host per
            // environment (environment.mainAppApiUrl). These routes forward the browser there.
            // "embedded" preserves the legacy DisplayResourceAsEmbeddedPDF in-browser behavior.
            {
                path: "file-resources/embedded/:fileResourceGuid",
                data: { redirectTo: `${environment.mainAppApiUrl}/file-resources/:fileResourceGuid?inline=true`, externalRedirect: true },
                loadComponent: () => import("./pages/legacy-redirect/legacy-redirect.component").then((m) => m.LegacyRedirectComponent),
            },
            {
                path: "file-resources/:fileResourceGuid",
                data: { redirectTo: `${environment.mainAppApiUrl}/file-resources/:fileResourceGuid`, externalRedirect: true },
                loadComponent: () => import("./pages/legacy-redirect/legacy-redirect.component").then((m) => m.LegacyRedirectComponent),
            },
            {
                path: "projects/five-year",
                title: "5-Year Project List",
                loadComponent: () => import("./areas/eip/projects-five-year/projects-five-year-list.component").then((m) => m.ProjectsFiveYearListComponent),
                data: { area: "eip" },
            },
            {
                path: "projects/completed",
                title: "Completed Project List",
                loadComponent: () => import("./areas/eip/projects-completed/projects-completed-list.component").then((m) => m.ProjectsCompletedListComponent),
                data: { area: "eip" },
            },
            {
                path: "eip-performance-measures",
                title: "EIP Performance Measures",
                loadComponent: () => import("./areas/eip/eip-performance-measures/eip-performance-measures-list.component").then((m) => m.EipPerformanceMeasuresListComponent),
                data: { area: "eip" },
            },
            {
                path: `eip-performance-measures/:${routeParams.eipPerformanceMeasureID}`,
                title: "EIP Performance Measure",
                loadComponent: () =>
                    import("./areas/eip/eip-performance-measures/eip-performance-measure-detail/eip-performance-measure-detail.component").then(
                        (m) => m.EipPerformanceMeasureDetailComponent
                    ),
                data: { area: "eip" },
            },
            {
                path: "taxonomy",
                title: "EIP Focus Areas, Programs, and Action Priorities",
                loadComponent: () => import("./areas/eip/taxonomy/taxonomy.component").then((m) => m.TaxonomyComponent),
                data: { area: "eip" },
            },
            {
                path: "program-info/threshold-categories",
                loadComponent: () => import("./areas/eip/threshold-categories.component").then((m) => m.ThresholdCategoriesComponent),
                data: { area: "eip" },
            },
            {
                path: "eip-programs",
                title: "EIP Programs",
                loadComponent: () => import("./areas/eip/eip-programs/eip-programs-list.component").then((m) => m.EipProgramsListComponent),
                data: { area: "eip" },
            },
            {
                path: `eip-programs/:${routeParams.eipProgramID}`,
                title: "EIP Program",
                loadComponent: () => import("./areas/eip/eip-programs/eip-program-detail/eip-program-detail.component").then((m) => m.EipProgramDetailComponent),
                data: { area: "eip" },
            },
            {
                path: "eip-focus-areas",
                title: "EIP Focus Areas",
                loadComponent: () => import("./areas/eip/eip-focus-areas/eip-focus-areas-list.component").then((m) => m.EipFocusAreasListComponent),
                data: { area: "eip" },
            },
            {
                path: `eip-focus-areas/:${routeParams.eipFocusAreaID}`,
                title: "EIP Focus Area",
                loadComponent: () => import("./areas/eip/eip-focus-areas/eip-focus-area-detail/eip-focus-area-detail.component").then((m) => m.EipFocusAreaDetailComponent),
                data: { area: "eip" },
            },
            {
                path: "eip-action-priorities",
                title: "EIP Action Priorities",
                loadComponent: () => import("./areas/eip/eip-action-priorities/eip-action-priorities-list.component").then((m) => m.EipActionPrioritiesListComponent),
                data: { area: "eip" },
            },
            {
                path: `eip-action-priorities/:${routeParams.eipActionPriorityID}`,
                title: "EIP Action Priority",
                loadComponent: () =>
                    import("./areas/eip/eip-action-priorities/eip-action-priority-detail/eip-action-priority-detail.component").then((m) => m.EipActionPriorityDetailComponent),
                data: { area: "eip" },
            },
            {
                path: "results/investment-by-sector",
                title: "EIP Investment by Funding Sector",
                loadComponent: () => import("./areas/eip/investment-by-funding-sector/investment-by-funding-sector.component").then((m) => m.InvestmentByFundingSectorComponent),
                data: { area: "eip" },
            },
            {
                path: "results/spending-by-sector-by-focus-area-by-program",
                title: "Spending by Sector, Focus Area, and Program",
                loadComponent: () =>
                    import("./areas/eip/spending-by-sector-by-focus-area-by-program/spending-by-sector-by-focus-area-by-program.component").then(
                        (m) => m.SpendingBySectorByFocusAreaByProgramComponent
                    ),
                data: { area: "eip" },
            },
            {
                path: "results/by-program",
                title: "EIP Results by Program",
                loadComponent: () => import("./areas/eip/results-by-program/results-by-program.component").then((m) => m.ResultsByProgramComponent),
                data: { area: "eip" },
            },
            {
                path: "results/snapshot",
                loadComponent: () => import("./areas/eip/results-snapshot.component").then((m) => m.ResultsSnapshotComponent),
                canActivate: [EipAdminAccessGuard],
                data: { area: "eip" },
            },
            {
                path: "results/spending-by-eip-performance-measure-by-project",
                title: "Spending by Project for Selected Performance Measure",
                loadComponent: () =>
                    import("./areas/eip/spending-by-eip-performance-measure-by-project/spending-by-eip-performance-measure-by-project.component").then(
                        (m) => m.SpendingByEIPPerformanceMeasureByProjectComponent
                    ),
                data: { area: "eip" },
            },
            {
                path: `featured-projects`,
                title: "Featured Projects",
                loadComponent: () => import("./areas/eip/featured-projects/featured-projects-list/featured-projects-list.component").then((m) => m.FeaturedProjectsListComponent),
                data: { area: "eip" },
            },
            {
                path: "projects/all",
                title: "Projects",
                loadComponent: () => import("./pages/projects/projects.component").then((m) => m.ProjectsComponent),
                data: { area: "main" },
            },
            {
                path: "projects/my-projects-and-proposals",
                title: "My Projects & Proposals",
                loadComponent: () => import("./pages/projects/projects-my.component").then((m) => m.ProjectsMyComponent),
                canActivate: [UnauthenticatedAccessGuard, ProjectUpdateViewAccessGuard],
                data: { area: "main" },
            },
            // Legacy short path retained so existing bookmarks/links keep working (LTINFO-2997)
            { path: "projects/my", redirectTo: "projects/my-projects-and-proposals", pathMatch: "full" },
            {
                path: "projects/proposed",
                title: "Proposed Projects",
                loadComponent: () => import("./pages/projects/projects-proposed.component").then((m) => m.ProjectsProposedComponent),
                canActivate: [UnauthenticatedAccessGuard, ProjectUpdateViewAccessGuard],
                data: { area: "main" },
            },
            {
                path: "project-updates",
                title: "My Projects Requiring An Update",
                loadComponent: () => import("./pages/projects/projects-requiring-update.component").then((m) => m.ProjectsRequiringUpdateComponent),
                canActivate: [UnauthenticatedAccessGuard, ProjectUpdateViewAccessGuard],
                data: { area: "main" },
            },
            {
                // User-facing "Annual Project Update Status Report" (legacy ProjectUpdate/ProjectUpdateStatus) — LTINFO-2997
                path: "project-update-status",
                title: "Project Update Status",
                loadComponent: () => import("./pages/projects/project-update-status.component").then((m) => m.ProjectUpdateStatusComponent),
                canActivate: [UnauthenticatedAccessGuard, ProjectUpdateViewAccessGuard],
                data: { area: "main" },
            },
            {
                path: "manage-project-updates",
                title: "Manage Project Updates",
                loadComponent: () => import("./pages/projects/manage-project-updates.component").then((m) => m.ManageProjectUpdatesComponent),
                data: { area: "main" },
            },
            {
                path: "page-content",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Page Content",
                loadComponent: () => import("./pages/page-content/page-content.component").then((m) => m.PageContentComponent),
                data: { area: "main" },
            },
            {
                path: "labels-and-definitions",
                title: "Labels and Definitions",
                loadComponent: () => import("./pages/field-definition-list/field-definition-list.component").then((m) => m.FieldDefinitionListComponent),
                data: { area: "main" },
            },
            {
                path: "tags",
                title: "Tags",
                loadComponent: () => import("./pages/tags/tags.component").then((m) => m.TagsComponent),
                data: { area: "main" },
            },
            {
                // STUB: legacy ProjectTag.Detail page (per-tag detail showing the projects
                // associated with the tag). Routed so existing links — e.g. the tag name
                // on the project-detail Related Projects card — have a real destination
                // and the parity gap isn't forgotten. Full port pending.
                path: "tags/:tagID",
                title: "Tag Detail",
                loadComponent: () => import("./pages/tags/tag-detail/tag-detail.component").then((m) => m.TagDetailComponent),
                data: { area: "main" },
            },
            {
                path: "users",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Users",
                loadComponent: () => import("./pages/users/manage-users.component").then((m) => m.ManageUsersComponent),
                data: { area: "main" },
            },
            {
                path: "users/:userID",
                title: "User Detail",
                loadComponent: () => import("./pages/users/user-detail/user-detail.component").then((m) => m.UserDetailComponent),
                data: { area: "main" },
            },
            {
                path: "roles",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Roles",
                loadComponent: () => import("./pages/roles/roles-list.component").then((m) => m.RolesListComponent),
                data: { area: "main" },
            },
            {
                path: "roles/:areaID/:roleID",
                title: "Role Detail",
                loadComponent: () => import("./pages/roles/role-detail/role-detail.component").then((m) => m.RoleDetailComponent),
                data: { area: "main" },
            },
            // Admin-create project page (single-page form, replaces legacy /Project/New)
            {
                path: "projects/create",
                title: "Create a New Project",
                loadComponent: () => import("./pages/projects/project-create/project-create.component").then((m) => m.ProjectCreateComponent),
                data: { area: "main" },
            },
            // Project Create Workflow: New project (only project-type step available)
            {
                path: "projects/new",
                title: "New Project",
                loadComponent: () =>
                    import("./pages/projects/project-create-workflow/project-create-workflow-outlet.component").then(
                        (m) => m.ProjectCreateWorkflowOutletComponent
                    ),
                data: { area: "main" },
                children: [
                    { path: "", redirectTo: "instructions", pathMatch: "full" },
                    {
                        path: "instructions",
                        loadComponent: () =>
                            import("./pages/projects/project-create-workflow/steps/instructions/instructions.component").then(
                                (m) => m.CreateInstructionsStepComponent
                            ),
                    },
                    {
                        path: "project-type",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-create-workflow/steps/project-type/project-type-step.component").then(
                                (m) => m.ProjectTypeStepComponent
                            ),
                    },
                ],
            },
            // Project Create Workflow: Edit draft/pending project (all steps available)
            {
                path: `projects/edit/:${routeParams.projectID}`,
                title: "Edit Project",
                loadComponent: () =>
                    import("./pages/projects/project-create-workflow/project-create-workflow-outlet.component").then(
                        (m) => m.ProjectCreateWorkflowOutletComponent
                    ),
                data: { area: "main" },
                children: [
                    { path: "", redirectTo: "instructions", pathMatch: "full" },
                    {
                        path: "instructions",
                        loadComponent: () =>
                            import("./pages/projects/project-create-workflow/steps/instructions/instructions.component").then(
                                (m) => m.CreateInstructionsStepComponent
                            ),
                    },
                    {
                        path: "project-type",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-create-workflow/steps/project-type/project-type-step.component").then(
                                (m) => m.ProjectTypeStepComponent
                            ),
                    },
                    {
                        path: "basics",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-create-workflow/steps/basics/basics-step.component").then(
                                (m) => m.BasicsStepComponent
                            ),
                    },
                    {
                        path: "location-simple",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-create-workflow/steps/location-simple/location-simple-step.component").then(
                                (m) => m.LocationSimpleStepComponent
                            ),
                    },
                    {
                        path: "location-detailed",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-create-workflow/steps/location-detailed/location-detailed-step.component").then(
                                (m) => m.LocationDetailedStepComponent
                            ),
                    },
                    {
                        path: "stormwater-questionnaire",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-create-workflow/steps/stormwater-questionnaire/stormwater-questionnaire-step.component").then(
                                (m) => m.StormwaterQuestionnaireStepComponent
                            ),
                    },
                    {
                        path: "performance-measures",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-create-workflow/steps/performance-measures/performance-measures-step.component").then(
                                (m) => m.PerformanceMeasuresStepComponent
                            ),
                    },
                    {
                        path: "expected-funding",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-create-workflow/steps/expected-funding/expected-funding-step.component").then(
                                (m) => m.ExpectedFundingStepComponent
                            ),
                    },
                    {
                        path: "transportation-assessment",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-create-workflow/steps/transportation-assessment/transportation-assessment-step.component").then(
                                (m) => m.TransportationAssessmentStepComponent
                            ),
                    },
                    {
                        path: "threshold-categories",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-create-workflow/steps/threshold-categories/threshold-categories-step.component").then(
                                (m) => m.ThresholdCategoriesStepComponent
                            ),
                    },
                    {
                        path: "photos",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-create-workflow/steps/photos/photos-step.component").then(
                                (m) => m.PhotosStepComponent
                            ),
                    },
                    {
                        path: "notes",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-create-workflow/steps/notes/notes-step.component").then(
                                (m) => m.NotesStepComponent
                            ),
                    },
                ],
            },
            // Project Update Workflow: Update approved project
            {
                path: `projects/:${routeParams.projectID}/update`,
                title: "Update Project",
                loadComponent: () =>
                    import("./pages/projects/project-update-workflow/project-update-workflow-outlet.component").then(
                        (m) => m.ProjectUpdateWorkflowOutletComponent
                    ),
                data: { area: "main" },
                children: [
                    { path: "", redirectTo: "instructions", pathMatch: "full" },
                    {
                        path: "instructions",
                        loadComponent: () =>
                            import("./pages/projects/project-update-workflow/steps/instructions/instructions.component").then(
                                (m) => m.UpdateInstructionsStepComponent
                            ),
                    },
                    {
                        path: "basics",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-update-workflow/steps/basics/basics.component").then(
                                (m) => m.UpdateBasicsStepComponent
                            ),
                    },
                    {
                        path: "location-simple",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-update-workflow/steps/location-simple/location-simple.component").then(
                                (m) => m.UpdateLocationSimpleStepComponent
                            ),
                    },
                    {
                        path: "location-detailed",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-update-workflow/steps/location-detailed/location-detailed.component").then(
                                (m) => m.UpdateLocationDetailedStepComponent
                            ),
                    },
                    {
                        path: "stormwater-questionnaire",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-update-workflow/steps/stormwater-questionnaire/stormwater-questionnaire.component").then(
                                (m) => m.UpdateStormwaterQuestionnaireStepComponent
                            ),
                    },
                    {
                        path: "performance-measures",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-update-workflow/steps/performance-measures/performance-measures.component").then(
                                (m) => m.UpdatePerformanceMeasuresStepComponent
                            ),
                    },
                    {
                        path: "expected-funding",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-update-workflow/steps/expected-funding/expected-funding.component").then(
                                (m) => m.UpdateExpectedFundingStepComponent
                            ),
                    },
                    {
                        path: "expenditures",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-update-workflow/steps/expenditures/expenditures.component").then(
                                (m) => m.UpdateExpendituresStepComponent
                            ),
                    },
                    {
                        path: "transportation-budgets",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-update-workflow/steps/transportation-budgets/transportation-budgets.component").then(
                                (m) => m.UpdateTransportationBudgetsStepComponent
                            ),
                    },
                    {
                        path: "photos",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-update-workflow/steps/photos/photos.component").then(
                                (m) => m.UpdatePhotosStepComponent
                            ),
                    },
                    {
                        path: "external-links",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-update-workflow/steps/external-links/external-links.component").then(
                                (m) => m.UpdateExternalLinksStepComponent
                            ),
                    },
                    {
                        path: "notes",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./pages/projects/project-update-workflow/steps/notes/notes.component").then(
                                (m) => m.UpdateNotesStepComponent
                            ),
                    },
                ],
            },
            {
                path: `projects/:${routeParams.projectID}`,
                title: "Projects",
                loadComponent: () => import("./pages/projects/project-detail/project-detail.component").then((m) => m.ProjectDetailComponent),
                data: { area: "main" },
            },
            // ...existing children for other areas and main site...

            {
                path: "stormwater-jurisdictions",
                title: "View All Stormwater Jurisdictions",
                loadComponent: () => import("./areas/stormwater/stormwater-jurisdictions/stormwater-jurisdictions.component").then((m) => m.StormwaterJurisdictionsComponent),
                data: { area: "stormwater" },
            },
            {
                path: `stormwater-jurisdictions/:stormwaterJurisdictionID`,
                title: "Stormwater Jurisdiction Detail",
                loadComponent: () =>
                    import("./areas/stormwater/stormwater-jurisdictions/stormwater-jurisdiction-detail/stormwater-jurisdiction-detail.component").then(
                        (m) => m.StormwaterJurisdictionDetailComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "modeled-catchments",
                title: "View All Modeled Catchments",
                loadComponent: () => import("./areas/stormwater/modeled-catchments/modeled-catchments.component").then((m) => m.ModeledCatchmentsComponent),
                data: { area: "stormwater" },
            },
            {
                path: `modeled-catchments/:modeledCatchmentID`,
                title: "Modeled Catchment Detail",
                loadComponent: () =>
                    import("./areas/stormwater/modeled-catchments/modeled-catchment-detail/modeled-catchment-detail.component").then((m) => m.ModeledCatchmentDetailComponent),
                data: { area: "stormwater" },
            },
            {
                path: "treatment-bmps",
                canActivate: [UnauthenticatedAccessGuard],
                title: "View All BMPs",
                loadComponent: () => import("./areas/stormwater/treatment-bmps/treatment-bmps.component").then((m) => m.TreatmentBmpsComponent),
                data: { area: "stormwater" },
            },
            {
                path: "treatment-bmps/new",
                title: "Create New BMP",
                canActivate: [UnauthenticatedAccessGuard, StormwaterEditAccessGuard],
                loadComponent: () => import("./areas/stormwater/treatment-bmps/create-treatment-bmp/create-treatment-bmp.component").then((m) => m.CreateTreatmentBmpComponent),
                canDeactivate: [UnsavedChangesGuard],
                data: { area: "stormwater" },
            },
            {
                path: `treatment-bmps/:${routeParams.treatmentBMPID}`,
                title: "Treatment BMP Detail",
                loadComponent: () => import("./areas/stormwater/treatment-bmps/treatment-bmp-detail/treatment-bmp-detail.component").then((m) => m.TreatmentBmpDetailComponent),
                data: { area: "stormwater" },
            },
            {
                path: `treatment-bmps/:${routeParams.treatmentBMPID}/edit`,
                title: "Edit Treatment BMP",
                canActivate: [UnauthenticatedAccessGuard, StormwaterEditAccessGuard],
                loadComponent: () => import("./areas/stormwater/treatment-bmps/create-treatment-bmp/create-treatment-bmp.component").then((m) => m.CreateTreatmentBmpComponent),
                canDeactivate: [UnsavedChangesGuard],
                data: { area: "stormwater" },
            },
            {
                path: `treatment-bmps/:${routeParams.treatmentBMPID}/versions/:${routeParams.treatmentBMPVersionID}`,
                title: "Treatment BMP Version Detail",
                loadComponent: () => import("./areas/stormwater/treatment-bmps/treatment-bmp-detail/treatment-bmp-detail.component").then((m) => m.TreatmentBmpDetailComponent),
                data: { area: "stormwater" },
            },
            {
                path: `treatment-bmps/:${routeParams.treatmentBMPID}/versions/:${routeParams.treatmentBMPVersionID}/benchmark-thresholds`,
                title: "Manage Benchmark & Thresholds",
                loadComponent: () =>
                    import("./areas/stormwater/treatment-bmps/treatment-bmp-benchmark-threshold-wizard/treatment-bmp-benchmark-threshold-wizard-outlet.component").then(
                        (m) => m.TreatmentBmpBenchmarkThresholdWizardOutletComponent
                    ),
                data: { area: "stormwater" },
                children: [
                    { path: "", redirectTo: "instructions", pathMatch: "full" },
                    {
                        path: "instructions",
                        title: "Instructions",
                        loadComponent: () =>
                            import("./areas/stormwater/treatment-bmps/treatment-bmp-benchmark-threshold-wizard/instructions/instructions.component").then(
                                (m) => m.BenchmarkThresholdInstructionsComponent
                            ),
                    },
                    {
                        path: `observation-types/:${routeParams.observationTypeID}`,
                        title: "Edit Observation Type",
                        loadComponent: () =>
                            import("./areas/stormwater/treatment-bmps/treatment-bmp-benchmark-threshold-wizard/edit-observation-type/edit-observation-type.component").then(
                                (m) => m.EditObservationTypeComponent
                            ),
                    },
                ],
            },
            {
                path: "road-segments",
                title: "View All Road Segments",
                loadComponent: () => import("./areas/stormwater/road-segments/road-segments.component").then((m) => m.RoadSegmentsComponent),
                data: { area: "stormwater" },
            },
            {
                path: "road-segments/new",
                title: "New Road Segment",
                canActivate: [UnauthenticatedAccessGuard, StormwaterEditAccessGuard],
                canDeactivate: [UnsavedChangesGuard],
                loadComponent: () =>
                    import("./areas/stormwater/road-segments/road-segment-edit/road-segment-edit.component").then(
                        (m) => m.RoadSegmentEditComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "road-segments/:roadSegmentID",
                title: "Road Segment Detail",
                loadComponent: () =>
                    import("./areas/stormwater/road-segments/road-segment-detail/road-segment-detail.component").then(
                        (m) => m.RoadSegmentDetailComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "road-segments/:roadSegmentID/edit",
                title: "Edit Road Segment",
                canActivate: [UnauthenticatedAccessGuard, StormwaterManageRoadSegmentAccessGuard],
                canDeactivate: [UnsavedChangesGuard],
                loadComponent: () =>
                    import("./areas/stormwater/road-segments/road-segment-edit/road-segment-edit.component").then(
                        (m) => m.RoadSegmentEditComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "road-classes",
                title: "View All Road Classes",
                loadComponent: () => import("./areas/stormwater/road-classes/road-classes.component").then((m) => m.RoadClassesComponent),
                data: { area: "stormwater" },
            },
            {
                path: "road-classes/:roadClassID",
                title: "Road Class Detail",
                loadComponent: () =>
                    import("./areas/stormwater/road-classes/road-class-detail/road-class-detail.component").then(
                        (m) => m.RoadClassDetailComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "observation-types",
                title: "View All Observation Types",
                loadComponent: () =>
                    import("./areas/stormwater/observation-types/observation-types.component").then(
                        (m) => m.ObservationTypesComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "observation-types/new",
                title: "New Observation Type",
                canActivate: [UnauthenticatedAccessGuard, StormwaterAdminAccessGuard],
                loadComponent: () =>
                    import("./areas/stormwater/observation-types/observation-type-edit/observation-type-edit.component").then(
                        (m) => m.ObservationTypeEditComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "observation-types/edit/:treatmentBMPObservationTypeID",
                title: "Edit Observation Type",
                canActivate: [UnauthenticatedAccessGuard, StormwaterAdminAccessGuard],
                loadComponent: () =>
                    import("./areas/stormwater/observation-types/observation-type-edit/observation-type-edit.component").then(
                        (m) => m.ObservationTypeEditComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "observation-types/:treatmentBMPObservationTypeID",
                title: "Observation Type Detail",
                loadComponent: () =>
                    import("./areas/stormwater/observation-types/observation-type-detail/observation-type-detail.component").then(
                        (m) => m.ObservationTypeDetailComponent
                    ),
                data: { area: "stormwater" },
            },
            // Treatment BMP Types
            {
                path: "treatment-bmp-types",
                title: "Treatment BMP Types",
                loadComponent: () =>
                    import("./areas/stormwater/treatment-bmp-types/treatment-bmp-types-index.component").then(
                        (m) => m.TreatmentBMPTypesIndexComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "manage/treatment-bmp-types",
                title: "Manage Treatment BMP Types",
                loadComponent: () =>
                    import("./areas/stormwater/treatment-bmp-types/treatment-bmp-types-manage.component").then(
                        (m) => m.TreatmentBMPTypesManageComponent
                    ),
                canActivate: [UnauthenticatedAccessGuard, StormwaterAdminAccessGuard],
                data: { area: "stormwater" },
            },
            {
                path: "treatment-bmp-types/:treatmentBMPTypeID",
                title: "Treatment BMP Type Detail",
                loadComponent: () =>
                    import("./areas/stormwater/treatment-bmp-types/treatment-bmp-type-detail/treatment-bmp-type-detail.component").then(
                        (m) => m.TreatmentBMPTypeDetailComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "road-assessment-observation-periods",
                title: "View All Road Assessment Observation Periods",
                loadComponent: () =>
                    import("./areas/stormwater/road-assessment-observation-periods/road-assessment-observation-periods.component").then(
                        (m) => m.RoadAssessmentObservationPeriodsComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "road-assessment-observation-periods/:roadAssessmentObservationPeriodID",
                title: "Observation Period Detail",
                loadComponent: () =>
                    import("./areas/stormwater/road-assessment-observation-periods/road-assessment-observation-period-detail/road-assessment-observation-period-detail.component").then(
                        (m) => m.RoadAssessmentObservationPeriodDetailComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "credit-targets",
                title: "View All Credit Targets",
                loadComponent: () =>
                    import("./areas/stormwater/credit-targets/credit-targets.component").then(
                        (m) => m.CreditTargetsComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "credit-targets/edit",
                title: "Set Credit Targets",
                loadComponent: () =>
                    import("./areas/stormwater/credit-targets/credit-targets-edit/credit-targets-edit.component").then(
                        (m) => m.CreditTargetsEditComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "how-credits-are-calculated",
                title: "How Credits Are Calculated",
                loadComponent: () =>
                    import("./areas/stormwater/program-info/how-credits-are-calculated.component").then(
                        (m) => m.HowCreditsAreCalculatedComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "release-notes",
                title: "Release Notes",
                loadComponent: () =>
                    import("./areas/stormwater/release-notes/release-notes.component").then(
                        (m) => m.ReleaseNotesComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "launch-pad",
                title: "Launch Pad",
                canActivate: [UnauthenticatedAccessGuard, StormwaterEditAccessGuard],
                loadComponent: () =>
                    import("./areas/stormwater/launch-pad/launch-pad.component").then(
                        (m) => m.LaunchPadComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "assessments",
                canActivate: [UnauthenticatedAccessGuard],
                title: "View All Assessments",
                loadComponent: () => import("./areas/stormwater/assessments/assessments.component").then((m) => m.AssessmentsComponent),
                data: { area: "stormwater" },
            },
            {
                path: "treatment-bmp-version-assessments/:treatmentBMPVersionAssessmentID",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Treatment BMP Assessment Detail",
                loadComponent: () =>
                    import("./areas/stormwater/treatment-bmp-version-assessments/treatment-bmp-version-assessment-detail/treatment-bmp-version-assessment-detail.component").then(
                        (m) => m.TreatmentBMPVersionAssessmentDetailComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "treatment-bmp-version-assessments/edit/:treatmentBMPVersionAssessmentID",
                title: "Edit Treatment BMP Assessment",
                canActivate: [UnauthenticatedAccessGuard, StormwaterEditAccessGuard],
                loadComponent: () =>
                    import(
                        "./areas/stormwater/treatment-bmp-version-assessments/workflow/treatment-bmp-version-assessment-workflow-outlet/treatment-bmp-version-assessment-workflow-outlet.component"
                    ).then((m) => m.TreatmentBMPVersionAssessmentWorkflowOutletComponent),
                data: { area: "stormwater" },
                children: [
                    { path: "", redirectTo: "assessment-information", pathMatch: "full" },
                    {
                        path: "assessment-information",
                        loadComponent: () =>
                            import("./areas/stormwater/treatment-bmp-version-assessments/workflow/assessment-information/assessment-information.component").then(
                                (m) => m.AssessmentInformationStepComponent
                            ),
                    },
                    {
                        path: "observations/:treatmentBMPObservationTypeID",
                        loadComponent: () =>
                            import("./areas/stormwater/treatment-bmp-version-assessments/workflow/observation-step/observation-step.component").then(
                                (m) => m.ObservationStepComponent
                            ),
                    },
                    {
                        path: "photos",
                        loadComponent: () =>
                            import("./areas/stormwater/treatment-bmp-version-assessments/workflow/photos/photos.component").then((m) => m.PhotosStepComponent),
                    },
                    {
                        path: "score",
                        loadComponent: () =>
                            import("./areas/stormwater/treatment-bmp-version-assessments/workflow/score/score.component").then((m) => m.ScoreStepComponent),
                    },
                ],
            },
            {
                path: "treatment-bmp-version-assessments/:treatmentBMPVersionAssessmentID/update-images",
                title: "Update Assessment Photos",
                canActivate: [UnauthenticatedAccessGuard, StormwaterEditAccessGuard],
                loadComponent: () =>
                    import("./areas/stormwater/treatment-bmp-version-assessments/treatment-bmp-version-assessment-detail/treatment-bmp-version-assessment-update-images/treatment-bmp-version-assessment-update-images.component").then(
                        (m) => m.TreatmentBmpVersionAssessmentUpdateImagesComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "parcel-bmp-assessments/:parcelBMPAssessmentID",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Parcel BMP Assessment Detail",
                loadComponent: () =>
                    import("./areas/stormwater/parcel-bmp-assessments/parcel-bmp-assessment-detail/parcel-bmp-assessment-detail.component").then(
                        (m) => m.ParcelBMPAssessmentDetailComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "parcel-bmp-assessments/:parcelBMPAssessmentID/edit",
                title: "Edit Parcel BMP Assessment",
                canActivate: [UnauthenticatedAccessGuard, StormwaterEditAccessGuard],
                loadComponent: () =>
                    import("./areas/stormwater/parcel-bmp-assessments/parcel-bmp-assessment-edit/parcel-bmp-assessment-edit.component").then(
                        (m) => m.ParcelBMPAssessmentEditComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "stormwater-registrations",
                title: "View All Stormwater Registrations",
                loadComponent: () => import("./areas/stormwater/stormwater-registrations/stormwater-registrations.component").then((m) => m.StormwaterRegistrationsComponent),
                data: { area: "stormwater" },
            },
            {
                path: "stormwater-credit-declarations",
                title: "View All Stormwater Credit Declarations",
                loadComponent: () =>
                    import("./areas/stormwater/stormwater-credit-declarations/stormwater-credit-declarations.component").then((m) => m.StormwaterCreditDeclarationsComponent),
                data: { area: "stormwater" },
            },
            {
                path: "credit-declarations/edit/:creditDeclarationID",
                title: "Edit Credit Declaration",
                loadComponent: () =>
                    import("./areas/stormwater/stormwater-credit-declarations/credit-declaration-edit/credit-declaration-edit.component").then(
                        (m) => m.CreditDeclarationEditComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "credit-declarations/:creditDeclarationID",
                title: "Credit Declaration Detail",
                loadComponent: () =>
                    import("./areas/stormwater/stormwater-credit-declarations/credit-declaration-detail/credit-declaration-detail.component").then(
                        (m) => m.CreditDeclarationDetailComponent
                    ),
                data: { area: "stormwater" },
            },
            {
                path: "stormwater-credit-distributions",
                title: "View All Stormwater Credit Distributions",
                loadComponent: () =>
                    import("./areas/stormwater/stormwater-credit-distributions/stormwater-credit-distributions.component").then((m) => m.StormwaterCreditDistributionsComponent),
                data: { area: "stormwater" },
            },
            {
                path: "manage/stormwater-users",
                title: "Stormwater User Management",
                canActivate: [UnauthenticatedAccessGuard, StormwaterAdminAccessGuard],
                loadComponent: () => import("./areas/stormwater/stormwater-users/stormwater-users.component").then((m) => m.StormwaterUsersComponent),
                data: { area: "stormwater" },
            },
            {
                path: "road-registrations/new",
                title: "Road Registrations",
                canActivate: [UnauthenticatedAccessGuard, StormwaterEditAccessGuard],
                loadComponent: () =>
                    import("./areas/stormwater/road-registrations/workflow/road-registration-workflow-outlet/road-registration-workflow-outlet.component").then(
                        (m) => m.RoadRegistrationWorkflowOutletComponent
                    ),
                children: [
                    { path: "", redirectTo: "registration-information", pathMatch: "full" },
                    {
                        path: "registration-information",
                        loadComponent: () =>
                            import("./areas/stormwater/road-registrations/workflow/registration-information/registration-information.component").then(
                                (m) => m.RegistrationInformationComponent
                            ),
                        data: { area: "stormwater" },
                    },
                ],
            },
            {
                path: "road-registrations/edit/:roadRegistrationID",
                // re-enter the workflow for an existing registration (workflow outlet with step children)
                canActivate: [UnauthenticatedAccessGuard, StormwaterEditAccessGuard],
                loadComponent: () =>
                    import("./areas/stormwater/road-registrations/workflow/road-registration-workflow-outlet/road-registration-workflow-outlet.component").then(
                        (m) => m.RoadRegistrationWorkflowOutletComponent
                    ),
                data: { area: "stormwater" },
                children: [
                    { path: "", redirectTo: "registration-information", pathMatch: "full" },
                    {
                        path: "registration-information",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./areas/stormwater/road-registrations/workflow/registration-information/registration-information.component").then(
                                (m) => m.RegistrationInformationComponent
                            ),
                    },
                    {
                        path: "registration-area",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./areas/stormwater/road-registrations/workflow/registration-area/registration-area.component").then((m) => m.RegistrationAreaComponent),
                    },
                    {
                        path: "upload-plrm-projects",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./areas/stormwater/road-registrations/workflow/upload-plrm-projects/upload-plrm-projects.component").then((m) => m.UploadPlrmProjectsComponent),
                    },
                    {
                        path: "register-road-classes",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./areas/stormwater/road-registrations/workflow/register-road-classes/register-road-classes.component").then(
                                (m) => m.RegisterRoadClassesComponent
                            ),
                    },
                    {
                        path: "propose-road-segments",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./areas/stormwater/road-registrations/workflow/propose-road-segments/propose-road-segments.component").then(
                                (m) => m.ProposeRoadSegmentsComponent
                            ),
                    },
                    {
                        path: "register-credits",
                        canDeactivate: [UnsavedChangesGuard],
                        loadComponent: () =>
                            import("./areas/stormwater/road-registrations/workflow/register-credits/register-credits.component").then((m) => m.RegisterCreditsComponent),
                    },
                ],
            },

            {
                path: "road-registrations/:roadRegistrationID",
                loadComponent: () =>
                    import("./areas/stormwater/road-registrations/road-registration-detail/road-registration-detail.component").then((m) => m.RoadRegistrationDetailComponent),
                data: { area: "stormwater" },
            },
            {
                path: "road-registrations/:roadRegistrationID/plrm-summary-report",
                title: "PLRM Summary Report",
                loadComponent: () =>
                    import("./areas/stormwater/plrm-summary-report/plrm-summary-report.component").then((m) => m.PlrmSummaryReportComponent),
                data: { area: "stormwater" },
            },

            // Road Registration Credit Declaration Edit (must be before detail to avoid :id matching "edit")
            {
                path: "road-registration-credit-declarations/edit/:roadRegistrationCreditDeclarationID",
                title: "Edit Road Registration Credit Declaration",
                canActivate: [UnauthenticatedAccessGuard, StormwaterEditAccessGuard],
                loadComponent: () =>
                    import(
                        "./areas/stormwater/road-registration-credit-declarations/road-registration-credit-declaration-edit/road-registration-credit-declaration-edit.component"
                    ).then((m) => m.RoadRegistrationCreditDeclarationEditComponent),
                data: { area: "stormwater" },
            },

            // Road Registration Credit Declaration Detail
            {
                path: "road-registration-credit-declarations/:roadRegistrationCreditDeclarationID",
                title: "Road Registration Credit Declaration Detail",
                loadComponent: () =>
                    import(
                        "./areas/stormwater/road-registration-credit-declarations/road-registration-credit-declaration-detail/road-registration-credit-declaration-detail.component"
                    ).then((m) => m.RoadRegistrationCreditDeclarationDetailComponent),
                data: { area: "stormwater" },
            },

            // Road Segment Assessment Detail
            {
                path: "road-segment-assessments/:roadSegmentAssessmentID",
                title: "Road Segment Assessment Detail",
                loadComponent: () =>
                    import(
                        "./areas/stormwater/road-segment-assessments/road-segment-assessment-detail/road-segment-assessment-detail.component"
                    ).then((m) => m.RoadSegmentAssessmentDetailComponent),
                data: { area: "stormwater" },
            },

            // Road Segment Assessment Workflow
            {
                path: "road-segment-assessments/new/:roadSegmentID",
                title: "New Road Segment Assessment",
                canActivate: [UnauthenticatedAccessGuard, StormwaterManageRoadSegmentAccessGuard],
                loadComponent: () =>
                    import(
                        "./areas/stormwater/road-segment-assessments/workflow/road-segment-assessment-workflow-outlet/road-segment-assessment-workflow-outlet.component"
                    ).then((m) => m.RoadSegmentAssessmentWorkflowOutletComponent),
                data: { area: "stormwater" },
                children: [
                    { path: "", redirectTo: "assessment-information", pathMatch: "full" },
                    {
                        path: "assessment-information",
                        loadComponent: () =>
                            import(
                                "./areas/stormwater/road-segment-assessments/workflow/assessment-information/assessment-information.component"
                            ).then((m) => m.AssessmentInformationComponent),
                    },
                ],
            },
            {
                path: "road-segment-assessments/edit/:roadSegmentAssessmentID",
                title: "Edit Road Segment Assessment",
                canActivate: [UnauthenticatedAccessGuard, StormwaterManageRoadSegmentAccessGuard],
                loadComponent: () =>
                    import(
                        "./areas/stormwater/road-segment-assessments/workflow/road-segment-assessment-workflow-outlet/road-segment-assessment-workflow-outlet.component"
                    ).then((m) => m.RoadSegmentAssessmentWorkflowOutletComponent),
                data: { area: "stormwater" },
                children: [
                    { path: "", redirectTo: "assessment-information", pathMatch: "full" },
                    {
                        path: "assessment-information",
                        loadComponent: () =>
                            import(
                                "./areas/stormwater/road-segment-assessments/workflow/assessment-information/assessment-information.component"
                            ).then((m) => m.AssessmentInformationComponent),
                    },
                    {
                        path: "accumulation-zones/:accumulationZoneID",
                        loadComponent: () =>
                            import(
                                "./areas/stormwater/road-segment-assessments/workflow/accumulation-zone-edit/accumulation-zone-edit.component"
                            ).then((m) => m.AccumulationZoneEditComponent),
                    },
                    {
                        path: "score",
                        loadComponent: () =>
                            import("./areas/stormwater/road-segment-assessments/workflow/score/score.component").then(
                                (m) => m.ScoreComponent
                            ),
                    },
                ],
            },
            {
                path: "bmp-registrations/new",
                title: "BMP Registrations",
                canActivate: [UnauthenticatedAccessGuard, StormwaterEditAccessGuard],
                loadComponent: () =>
                    import("./areas/stormwater/bmp-registrations/workflow/bmp-registration-workflow-outlet/bmp-registration-workflow-outlet.component").then(
                        (m) => m.BMPRegistrationWorkflowOutletComponent
                    ),
                children: [
                    { path: "", redirectTo: "registration-information", pathMatch: "full" },
                    {
                        path: "registration-information",
                        loadComponent: () =>
                            import("./areas/stormwater/bmp-registrations/workflow/registration-information/registration-information.component").then(
                                (m) => m.RegistrationInformationComponent
                            ),
                        data: { area: "stormwater" },
                    },
                ],
            },
            {
                path: "bmp-registrations/edit/:bmpRegistrationID",
                canActivate: [UnauthenticatedAccessGuard, StormwaterEditAccessGuard],
                loadComponent: () =>
                    import("./areas/stormwater/bmp-registrations/workflow/bmp-registration-workflow-outlet/bmp-registration-workflow-outlet.component").then(
                        (m) => m.BMPRegistrationWorkflowOutletComponent
                    ),
                data: { area: "stormwater" },
                children: [
                    { path: "", redirectTo: "registration-information", pathMatch: "full" },
                    {
                        path: "registration-information",
                        loadComponent: () =>
                            import("./areas/stormwater/bmp-registrations/workflow/registration-information/registration-information.component").then(
                                (m) => m.RegistrationInformationComponent
                            ),
                    },
                    {
                        path: "registration-area",
                        loadComponent: () =>
                            import("./areas/stormwater/bmp-registrations/workflow/registration-area/registration-area.component").then((m) => m.RegistrationAreaComponent),
                    },
                    {
                        path: "upload-plrm-projects",
                        loadComponent: () =>
                            import("./areas/stormwater/bmp-registrations/workflow/upload-plrm-projects/upload-plrm-projects.component").then((m) => m.UploadPlrmProjectsComponent),
                    },
                    {
                        path: "register-bmps",
                        loadComponent: () =>
                            import("./areas/stormwater/bmp-registrations/workflow/register-bmps/register-bmps.component").then((m) => m.RegisterBmpsComponent),
                    },
                    {
                        path: "parcel-bmps",
                        loadComponent: () =>
                            import("./areas/stormwater/bmp-registrations/workflow/parcel-bmps/parcel-bmps.component").then((m) => m.ParcelBmpsComponent),
                    },
                    {
                        path: "other-strategies",
                        loadComponent: () =>
                            import("./areas/stormwater/bmp-registrations/workflow/other-strategies/other-strategies.component").then((m) => m.OtherStrategiesComponent),
                    },
                    {
                        path: "register-credits",
                        loadComponent: () =>
                            import("./areas/stormwater/bmp-registrations/workflow/register-credits/register-credits.component").then((m) => m.RegisterCreditsComponent),
                    },
                ],
            },

            {
                path: "bmp-registrations/:bmpRegistrationID",
                loadComponent: () =>
                    import("./areas/stormwater/bmp-registrations/bmp-registration-detail/bmp-registration-detail.component").then((m) => m.BMPRegistrationDetailComponent),
                data: { area: "stormwater" },
            },
            {
                path: "bmp-registrations/:bmpRegistrationID/plrm-summary-report",
                title: "PLRM Summary Report",
                loadComponent: () =>
                    import("./areas/stormwater/plrm-summary-report/plrm-summary-report.component").then((m) => m.PlrmSummaryReportComponent),
                data: { area: "stormwater" },
            },

            // clarity area-specific routes
            {
                path: "resources/urban-stormwater-crediting-program",
                loadComponent: () =>
                    import("./areas/clarity/urban-stormwater-crediting-program/urban-stormwater-crediting-program.component").then(
                        (m) => m.UrbanStormwaterCreditingProgramComponent
                    ),
                data: { area: "clarity" },
            },
            {
                path: "resources/non-urban-performance-metrics",
                loadComponent: () =>
                    import("./areas/clarity/non-urban-performance-metrics/non-urban-performance-metrics.component").then((m) => m.NonUrbanPerformanceMetricsComponent),
                data: { area: "clarity" },
            },
            {
                path: "resources/tmdl-program-management",
                loadComponent: () => import("./areas/clarity/tmdl-program-management/tmdl-program-management.component").then((m) => m.TmdlProgramManagementComponent),
                data: { area: "clarity" },
            },
            {
                path: "feedback",
                loadComponent: () => import("./areas/clarity/support-request-logs/support-request-logs.component").then((m) => m.SupportRequestLogsComponent),
                data: { area: "clarity" },
            },
            {
                path: "lake-clarity-projects",
                loadComponent: () => import("./areas/clarity/lake-clarity-projects/lake-clarity-projects.component").then((m) => m.LakeClarityProjectsComponent),
                data: { area: "clarity" },
            },
            {
                path: "resources/documents",
                loadComponent: () => import("./areas/clarity/document-list/document-list.component").then((m) => m.default),
                data: { area: "clarity" },
            },
            {
                path: "results/overview",
                loadComponent: () => import("./areas/clarity/results-overview/results-overview.component").then((m) => m.ResultsOverviewComponent),
                data: { area: "clarity" },
            },
            {
                path: "results/urban-uplands",
                loadComponent: () =>
                    import("./areas/clarity/lake-clarity-indicator-detail/lake-clarity-indicator-detail.component").then((m) => m.LakeClarityIndicatorDetailComponent),
                data: { area: "clarity", lakeClarityTrackerCategoryID: LakeClarityTrackerCategoryEnum.UrbanUplands },
            },
            {
                path: "results/atmospheric-deposition",
                loadComponent: () =>
                    import("./areas/clarity/lake-clarity-indicator-detail/lake-clarity-indicator-detail.component").then((m) => m.LakeClarityIndicatorDetailComponent),
                data: { area: "clarity", lakeClarityTrackerCategoryID: LakeClarityTrackerCategoryEnum.AtmosphericDeposition },
            },
            {
                path: "results/stream-channel-erosion",
                loadComponent: () =>
                    import("./areas/clarity/lake-clarity-indicator-detail/lake-clarity-indicator-detail.component").then((m) => m.LakeClarityIndicatorDetailComponent),
                data: { area: "clarity", lakeClarityTrackerCategoryID: LakeClarityTrackerCategoryEnum.StreamChannelErosion },
            },
            {
                path: "results/forest-uplands",
                loadComponent: () =>
                    import("./areas/clarity/lake-clarity-indicator-detail/lake-clarity-indicator-detail.component").then((m) => m.LakeClarityIndicatorDetailComponent),
                data: { area: "clarity", lakeClarityTrackerCategoryID: LakeClarityTrackerCategoryEnum.ForestUplands },
            },
            {
                path: "results/water-clarity",
                loadComponent: () =>
                    import("./areas/clarity/lake-clarity-indicator-detail/lake-clarity-indicator-detail.component").then((m) => m.LakeClarityIndicatorDetailComponent),
                data: { area: "clarity", lakeClarityTrackerCategoryID: LakeClarityTrackerCategoryEnum.WaterClarity },
            },
            // Marketplace area-specific routes
            {
                path: "training",
                title: "Training",
                loadComponent: () => import("./areas/marketplace/training/training.component").then((m) => m.MarketplaceTrainingComponent),
                data: { area: "marketplace" },
            },
            {
                path: "help",
                title: "Help",
                loadComponent: () => import("./areas/marketplace/help/help.component").then((m) => m.MarketplaceHelpComponent),
                data: { area: "marketplace" },
            },
            {
                // Shared route (no data.area): the Conversion Calculator is linked from both the
                // Parcel Tracker (Results menu) and TDR Marketplace areas. Leaving it area-agnostic
                // makes [areaLink] navigate in-place within whichever subdomain you're on, so the page
                // keeps the current area's branding instead of bouncing everyone to the marketplace
                // subdomain. Layout is chosen by subdomain in getAreaLayoutComponent(), not by this data.
                path: "conversion-calculator",
                title: "Conversion Calculator",
                loadComponent: () => import("./areas/marketplace/conversion-calculator/conversion-calculator.component").then((m) => m.ConversionCalculatorComponent),
            },
            {
                path: "my-listings",
                title: "My Listings",
                loadComponent: () => import("./areas/marketplace/listings/my-listings/my-listings.component").then((m) => m.MyListingsComponent),
                data: { area: "marketplace" },
            },
            {
                path: "manage-listings",
                canActivate: [UnauthenticatedAccessGuard],
                title: "Manage Listings",
                loadComponent: () => import("./areas/marketplace/listings/manage-listings/manage-listings.component").then((m) => m.ManageListingsComponent),
                data: { area: "marketplace" },
            },
            {
                path: "listings",
                loadComponent: () => import("./areas/marketplace/listings/listings-layout.component").then((m) => m.ListingsLayoutComponent),
                data: { area: "marketplace" },
                children: [
                    {
                        path: "",
                        title: "All Listings",
                        loadComponent: () => import("./areas/marketplace/listings/listing-list/listing-list.component").then((m) => m.ListingListComponent),
                    },
                    {
                        path: "new",
                        loadComponent: () =>
                            import("./areas/marketplace/listings/workflow/listing-workflow-outlet/listing-workflow-outlet.component").then(
                                (m) => m.ListingWorkflowOutletComponent
                            ),
                        children: [
                            { path: "", redirectTo: "instructions", pathMatch: "full" },
                            {
                                path: "instructions",
                                title: "Create Listing - Instructions",
                                loadComponent: () =>
                                    import("./areas/marketplace/listings/workflow/listing-instructions/listing-instructions.component").then(
                                        (m) => m.ListingInstructionsComponent
                                    ),
                            },
                            {
                                path: "basics",
                                title: "Create Listing - Basics",
                                loadComponent: () =>
                                    import("./areas/marketplace/listings/workflow/listing-basics/listing-basics.component").then((m) => m.ListingBasicsComponent),
                            },
                            {
                                path: "offer-details",
                                title: "Create Listing - Offer Details",
                                loadComponent: () =>
                                    import("./areas/marketplace/listings/workflow/listing-offer-details/listing-offer-details.component").then(
                                        (m) => m.ListingOfferDetailsComponent
                                    ),
                            },
                        ],
                    },
                    {
                        path: ":tdrListingID",
                        title: "Listing Detail",
                        loadComponent: () =>
                            import("./areas/marketplace/listings/listing-detail/listing-detail.component").then((m) => m.ListingDetailComponent),
                    },
                    {
                        path: ":tdrListingID/edit",
                        loadComponent: () =>
                            import("./areas/marketplace/listings/workflow/listing-workflow-outlet/listing-workflow-outlet.component").then(
                                (m) => m.ListingWorkflowOutletComponent
                            ),
                        children: [
                            { path: "", redirectTo: "instructions", pathMatch: "full" },
                            {
                                path: "instructions",
                                title: "Edit Listing - Instructions",
                                loadComponent: () =>
                                    import("./areas/marketplace/listings/workflow/listing-instructions/listing-instructions.component").then(
                                        (m) => m.ListingInstructionsComponent
                                    ),
                            },
                            {
                                path: "basics",
                                title: "Edit Listing - Basics",
                                loadComponent: () =>
                                    import("./areas/marketplace/listings/workflow/listing-basics/listing-basics.component").then((m) => m.ListingBasicsComponent),
                            },
                            {
                                path: "offer-details",
                                title: "Edit Listing - Offer Details",
                                loadComponent: () =>
                                    import("./areas/marketplace/listings/workflow/listing-offer-details/listing-offer-details.component").then(
                                        (m) => m.ListingOfferDetailsComponent
                                    ),
                            },
                        ],
                    },
                ],
            },
            // Legacy Corral MVC URL redirects (LTINFO-2993). Declared last so they only ever catch shapes
            // no real route claims, but still inside the area layout children so the area shell renders
            // during the redirect tick, and ahead of the "**" catch-all that would otherwise show "not found".
            ...legacyRedirectRoutes,
            // Error/status pages live inside the area layout so they render with the area header, nav, and
            // the standard ".site-wrapper" page inset instead of flush against the viewport corner. They must
            // come BEFORE ":OutcomeSlug", which matches any single segment and would otherwise swallow them.
            { path: "not-found", loadComponent: () => import("./shared/pages").then((m) => m.NotFoundComponent) },
            { path: "unauthenticated", loadComponent: () => import("./shared/pages").then((m) => m.UnauthenticatedComponent) },
            { path: "subscription-insufficient", loadComponent: () => import("./shared/pages").then((m) => m.SubscriptionInsufficientComponent) },
            {
                path: ":OutcomeSlug",
                loadComponent: () => {
                    const subdomainService = inject(SubdomainService);
                    const subdomain = subdomainService.getSubdomain();
                    switch (subdomain) {
                        case "climate":
                            return import("./areas/climate/outcome-detail/outcome-detail.component").then((m) => m.OutcomeDetailComponent);
                        default:
                            return import("./shared/pages").then((m) => m.NotFoundComponent);
                    }
                },
                data: { area: "climate" },
            },
            // Catch-all, last child. Because it lives inside the layout route, that route now matches every
            // URL — any route declared after the layout route at the top level would be unreachable, which is
            // why the error/status pages above had to move in here too.
            { path: "**", loadComponent: () => import("./shared/pages").then((m) => m.NotFoundComponent) },
        ],
    },
];
